// src/Reports.jsx
import { useEffect, useMemo, useState, useRef } from "react";
import { auth, db } from "./firebase";
import { collection, getDocs, query, where, doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import {
  computeBudgetForRange,
  computeBudgetForMonthList,
  computeRowBudgetForMonth,
  parseKey,
  parseMonthKey,
  toMonthKey,
  makeKey,
} from "./lib/budgetMath";
import { getSubcategoryStatus } from "./getSubcategoryStatus";
import {
  loadClassifiedRows,
  sumTotals,
  groupMonthly,
  computeBVA,
} from "./lib/reportingCore";

/* ===== utils ===== */
const toUSD = (n) =>
  (n ?? 0).toLocaleString(undefined, { style: "currency", currency: "USD" });

const isValidDate = (d) => d instanceof Date && !isNaN(d.getTime());
const fmtInput = (d) => (isValidDate(d) ? d.toISOString().slice(0, 10) : "");
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

const LINE_COLORS = ["#00B074","#0ea5e9","#f97316","#8b5cf6","#ef4444","#10b981","#eab308","#14b8a6","#f43f5e","#3b82f6"];

const TRELLI_GREEN = "#00B074";
const TRELLI_RED = "#B00020";
const TRELLI_GOLD = "#F4C542";

// simple savings color (solid brand gold by label)
const savingsColorFor = (_name) => TRELLI_GOLD;

// Shade ramps (dark → light)
const GREEN_SHADES = ["#004E3A", "#006A4F", "#008764", "#00A579", "#00B074", "#33C092", "#66D0AF", "#99E1CB"];
const RED_SHADES   = ["#5E0010", "#7D0016", "#9C001C", "#B00020", "#C53A4A", "#D8646D", "#E98E90", "#F0B6B8"];

const hashLabel = (s) => Array.from(String(s)).reduce((acc, ch) => (acc * 33 + ch.charCodeAt(0)) >>> 0, 0);
const incomeColorFor  = (name) => GREEN_SHADES[hashLabel(name) % GREEN_SHADES.length];
const expenseColorFor = (name) => RED_SHADES[hashLabel(name) % RED_SHADES.length];

function defaultRangeLast3Mo() {
  const now = new Date();
  // Last 3 FULL months: end = last day of previous month
  const endPrev = new Date(now.getFullYear(), now.getMonth(), 0); // e.g., Aug 31
  const startFirst = new Date(endPrev.getFullYear(), endPrev.getMonth() - 2, 1); // e.g., Jun 1
  return { start: startFirst, end: endPrev };
}

// Infer whether a group label is income- or expense-like based on the signed sum
function inferGroupTypeFromRows(name, isSub, drivingRows) {
  let sum = 0;
  for (const tx of drivingRows) {
    const cat = tx.category || "(Uncategorized)";
    const sub = tx.subcategory || "(Uncategorized)";
    const match = isSub ? (`${cat}: ${sub}` === name) : (cat === name);
    if (!match) continue;
    sum += Number(tx.amount || 0); // income +, expenses -
  }
  return sum >= 0 ? "income" : "expense";
}

// normalize labels so budgets ↔ tx match reliably
function normalizeLabel(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

// ---- goals helpers ----
const num = (v) =>
  typeof v === "number" ? v : (typeof v === "string" ? (parseFloat(v.replace(/,/g, "")) || 0) : 0);

// Inclusive month difference: (fromY,fromM) .. (toY,toM), both inclusive.
// Returns at least 1 (avoid divide-by-zero).
function monthsDiffInclusive(fromY, fromM, toY, toM) {
  const diff = (toY - fromY) * 12 + (toM - fromM) + 1;
  return diff <= 0 ? 1 : diff;
}

// obvious non-budget names (normalized)
const NON_BUDGET_CATEGORY_NAMES = [
  "Transfers & Reimbursements",
  "Transfers & Payments (Not in Budget)",
  "Items That Do Not Impact Budget",
  "Non-Budget",
];
const NON_BUDGET_SET = new Set(NON_BUDGET_CATEGORY_NAMES.map(normalizeLabel));

// prefer your matched labels
function pickCatSub(d) {
  const cm = d?.categoryMatch || {};
  const catFromMatch = (cm.category || "").trim();
  const subFromMatch = (cm.subcategory || "").trim();
  if (catFromMatch) {
    return {
      category: catFromMatch,
      subcategory: subFromMatch || String(d?.subcategory || ""),
    };
  }
  if (typeof d?.category === "string" && d.category.trim()) {
    return { category: d.category, subcategory: String(d?.subcategory || "") };
  }
  if (Array.isArray(d?.category) && d.category.length) {
    const label = String(d.category[d.category.length - 1] ?? d.category[0]);
    return { category: label, subcategory: String(d?.subcategory || "") };
  }
  return { category: String(d?.category || ""), subcategory: String(d?.subcategory || "") };
}

function expectedToDateForSub(sub, isIncome) {
  // We only need meta.expectedForRollup; it doesn’t depend on "actual" here
  const { meta } = getSubcategoryStatus({
    subcategory: sub,
    actual: 0,
    isIncome
  });
  return Number(meta?.expectedForRollup || 0);
}

// build type index from budgets (cat, cat::sub -> type)
function buildTypeIndexFromBudgets(budgetDocs) {
  const ix = new Map();
  for (const d of budgetDocs) {
    const b = d.data();
    const cat = b?.category;
    const catNorm = normalizeLabel(cat);
    const type = String(b?.type || "").trim().toLowerCase(); // income | expense | savings | non-budget
    if (catNorm && type) ix.set(catNorm, type);
    const subs = Array.isArray(b?.subcategories) ? b.subcategories : [];
    for (const s of subs) {
      const subNorm = normalizeLabel(s?.name);
      if (catNorm && subNorm && type) ix.set(`${catNorm}::${subNorm}`, type);
    }
  }
  return ix;
}

function classifyType(tx, typeIx) {
  const catNorm = normalizeLabel(tx.category);
  const subNorm = normalizeLabel(tx.subcategory);
  if (catNorm && subNorm) {
    const t = typeIx.get(`${catNorm}::${subNorm}`);
    if (t) return t;
  }
  if (catNorm) {
    const t = typeIx.get(catNorm);
    if (t) return t;
  }
  return null; // unmapped
}

// yyyy-mm key
function ymKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function monthsTrailing12(endDate) {
  const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
  const arr = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(end);
    d.setMonth(end.getMonth() - i);
    arr.push(ymKey(d));
  }
  return arr;
}

// YYYY-MM-DD array from start → end (inclusive)
function daysRange(startDate, endDate) {
  const start = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  const end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
  const arr = [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    arr.push(
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
    );
  }
  return arr;
}

function monthsBetweenKeys(startDate, endDate) {
  const start = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
  const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
  const arr = [];
  for (let d = new Date(start); d <= end; d.setMonth(d.getMonth() + 1)) {
    arr.push(ymKey(d));
  }
  return arr;
}

// Unified label helpers
function formatKeyLabel(k) {
  const parts = String(k).split("-");
  if (parts.length === 3) {
    // daily: YYYY-MM-DD -> DD/MM
    const y = +parts[0], m = +parts[1] - 1, d = +parts[2];
    const dt = new Date(y, m, d);
    return dt.toLocaleDateString(undefined, { day: "2-digit", month: "2-digit" });
  }
  if (parts.length === 2) {
    // monthly: YYYY-MM -> MMM
    const y = +parts[0], m = +parts[1] - 1;
    return new Date(y, m, 1).toLocaleDateString(undefined, { month: "short" });
  }
  return String(k);
}

function formatMonthKey(k) {
  try {
    const { y, m } = parseMonthKey(k); // m is 0-based
    return new Date(y, m, 1).toLocaleDateString(undefined, { month: "short" });
  } catch {
    return String(k);
  }
}

// ===== Trends helpers =====
const TREND_MIN_POINTS = 3;
const TREND_AMT_THRESHOLD = 25;   // $25
const TREND_PCT_THRESHOLD = 0.05; // 5%

function normalizeLabelSimple(s) { return String(s || "").trim().toLowerCase(); }

function trendStats(series) {
  // series = [v1, v2, ... vN] aligned by period keys
  if (!Array.isArray(series) || series.length < TREND_MIN_POINTS) return null;
  const first = series[0] ?? 0;
  const last  = series[series.length - 1] ?? 0;
  const delta = last - first;
  const base  = Math.max(Math.abs(first), 1e-6);
  const pct   = delta / base;
  const significant = Math.abs(delta) >= TREND_AMT_THRESHOLD || Math.abs(pct) >= TREND_PCT_THRESHOLD;
  return { first, last, delta, pct, significant };
}

// Build keys for trends: monthly for all presets except "This Month" (daily).
function buildTrendKeys(preset, start, end) {
  const useDaily = preset === "thisMonth";
  return useDaily ? daysRange(start, end) : monthsBetweenKeys(start, end);
}

// For budgets inside Trends we always aggregate by month (even if daily view),
// and we use MTD for the current month if it's included and "Completed months only" is OFF.
function buildBudgetMonthKeys(preset, start, end) {
  const span = monthsBetweenKeys(start, end);
  if (!span.length) return span;
  return span;
}

// Sum period budgets per category/subcategory across included months.
// - respects category/subcategory filters via keepBudgetEntry
// - uses expectedToDateForSub for the *current* month when includesCurrentMonthMTD is true
function sumPeriodBudgetsByGroup(monthKeys, budgetDocs, includesCurrentMonthMTD, keepBudgetEntry, expectedToDateForSub) {
  const nowKey = ymKey(new Date());
  const catTotals = new Map(); // cat -> number
  const subTotals = new Map(); // "cat::sub" -> number

  // Pre-normalize doc categories for faster compares
  for (const b of budgetDocs) {
    const type = String(b?.type || "").toLowerCase();
    const category = b?.category || "";
    if (!category || type === "non-budget") continue;
    const subs = Array.isArray(b?.subcategories) ? b.subcategories : [];

    for (const sub of subs) {
      const subName = sub?.name || "";
      for (const mKey of monthKeys) {
        let amt = 0;
        if (includesCurrentMonthMTD && mKey === nowKey) {
          const isIncome = type === "income";
          amt = Number(expectedToDateForSub(sub, isIncome) || 0);
        } else {
          const { y, m } = parseMonthKey(mKey);
          const v = computeRowBudgetForMonth(sub, y, m, {
        strictNonNormalizedFallbackToNormalize: true,
      });
          amt = Number(isNaN(v) ? 0 : v);
        }

        if (!keepBudgetEntry(type, category, subName)) continue;

        const kCat = category;
        const kSub = `${category}::${subName}`;
        catTotals.set(kCat, (catTotals.get(kCat) || 0) + amt);
        subTotals.set(kSub, (subTotals.get(kSub) || 0) + amt);
      }
    }
  }
  return { catTotals, subTotals };
}

// --- add below monthsBetweenKeys ---
function addMonths(baseDate, n) {
  const d = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
  d.setMonth(d.getMonth() + n);
  return d;
}
const fmtMonthYear = (d) =>
  d.toLocaleDateString(undefined, { year: "numeric", month: "short" });

// Prefer a snapshot map for a month; otherwise compute live for that month
function getBudgetMapForMonthKey(mKey, budgetSnaps, budgetDocs) {
  const snap = budgetSnaps.get(mKey);
  if (snap instanceof Map) return snap;
  const { y, m } = parseMonthKey(mKey); // m is 0-based
  return computeBudgetForMonthList(budgetDocs, y, m);
}

// Build a display tree from budgets (for filter UI)
function buildBudgetTree(budgetDocs) {
  const cats = [];
  for (const d of budgetDocs) {
    const b = d.data();
    const category = b?.category || "";
    const type = String(b?.type || "").toLowerCase();
    // skip non-budget from filter choices by default
    if (!category || type === "non-budget") continue;
    const subs = Array.isArray(b?.subcategories) ? b.subcategories : [];
    cats.push({
      category,
      categoryNorm: normalizeLabel(category),
      type,
      subcategories: subs.map((s) => ({
        name: s?.name || "",
        nameNorm: normalizeLabel(s?.name || ""),
      })),
    });
  }
  // sort by category name for stable UI
  cats.sort((a, b) => a.category.localeCompare(b.category));
  return cats;
}

/* ===== component ===== */
export default function Reports() {
  const user = auth.currentUser;
  const [start, setStart] = useState(defaultRangeLast3Mo().start);
  const [end, setEnd] = useState(defaultRangeLast3Mo().end);
  const [loading, setLoading] = useState(false);

  // Step 2/3 UI state
  const [confirmedOnly, setConfirmedOnly] = useState(true);
  const [preset, setPreset] = useState("last3");   // highlight the Last 3 Mo pill on load

  // Category filter UI state
  const [showCatPanel, setShowCatPanel] = useState(false);
  const [includeChildren, setIncludeChildren] = useState(true);
  const [selectedCats, setSelectedCats] = useState(() => new Set());        // normalized category names
  const [selectedSubs, setSelectedSubs] = useState(() => new Set());        // "catNorm::subNorm"

  // Presets helper: sets dates and highlights the active pill
  const applyPreset = (key, completedOnlyArg = bvaCompletedOnly) => {
    const now = new Date();

    if (key === "thisMonth") {
      // Always the active month (daily)
      setStart(new Date(now.getFullYear(), now.getMonth(), 1));
      setEnd(now);
      setBvaCompletedOnly(false); // MTD mode for BvA
    } else if (key === "last3") {
      if (completedOnlyArg) {
        // Last 3 FULL months: end = last day of previous month; start = first of month 2 months earlier
        const endPrev = new Date(now.getFullYear(), now.getMonth(), 0); // e.g., Aug 31
        const startFirst = new Date(endPrev.getFullYear(), endPrev.getMonth() - 2, 1); // e.g., Jun 1
        setStart(startFirst);
        setEnd(endPrev);
      } else {
        // Monthly buckets with a partial current month: start at 1st two months ago, end today
        const startFirst = new Date(now.getFullYear(), now.getMonth() - 2, 1);
        setStart(startFirst);
        setEnd(now);
      }
    } else if (key === "ytd") {
      // Year-to-date: when completedOnlyArg=true end at last day of previous month,
      // otherwise end today. Start is Jan 1 either way.
      const jan1 = new Date(now.getFullYear(), 0, 1);
      if (completedOnlyArg) {
        const endPrev = new Date(now.getFullYear(), now.getMonth(), 0); // e.g., Aug 31
        setStart(jan1);
        setEnd(endPrev);
      } else {
        setStart(jan1);
        setEnd(now);
      }
      setBvaCompletedOnly(completedOnlyArg);
    }

    setPreset(key);
  };

  // results
  const [rows, setRows] = useState([]); // included tx (after type mapping + non-budget exclusion + confirmed filter)
  const [totals, setTotals] = useState({ income: 0, expenses: 0, net: 0 });
  const [metaCounts, setMetaCounts] = useState({
    excludedNonBudget: 0,
    excludedNonBudgetByName: 0,
    unmappedUsedSignFallback: 0,
  });

  // monthly (for line/table)
  const [monthly, setMonthly] = useState([]); // [{month, income, expenses, net}]
  const [t12, setT12] = useState([]); // trailing keys

  // Keys the charts use (daily for This Month, monthly otherwise)
  const periodKeys = t12;

  // budgets for filter UI
  const [budgetTree, setBudgetTree] = useState([]);

  // raw budget docs for BvA math
  const [budgetDocs, setBudgetDocs] = useState([]);
  // BvA toggle: show completed months only (default ON)
  const [bvaCompletedOnly, setBvaCompletedOnly] = useState(true);

  // Completed-month budget snapshots (monthKey -> Map<key, amount>)
  const [budgetSnaps, setBudgetSnaps] = useState(new Map());
  // Avoid re-writing the same month repeatedly in one session
  const [snapTried, setSnapTried] = useState(new Set());

  const canRun = useMemo(
    () => !!user && isValidDate(start) && isValidDate(end),
    [user, start, end]
  );
  const hasAnyFilter = selectedCats.size > 0 || selectedSubs.size > 0;

  // Combine or Compare when multiple filters are selected
  const [compareMode, setCompareMode] = useState("combine"); // "combine" | "compare"
  const selectedCount = selectedSubs.size > 0 ? selectedSubs.size : selectedCats.size;

  // If the user drops to ≤1 selection, force back to combine
  useEffect(() => {
    if (selectedCount <= 1 && compareMode !== "combine") setCompareMode("combine");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCount]);

  const run = async () => {
    if (!canRun) return;
    setLoading(true);
    try {
      // 1) Budgets (kept: your existing fetch so the filter tree works exactly the same)
      const budgetsQ = query(collection(db, "budgets"), where("userId", "==", user.uid));
      const budgetsSnap = await getDocs(budgetsQ);
      setBudgetTree(buildBudgetTree(budgetsSnap.docs));
      const budgetDocsPlain = budgetsSnap.docs.map(d => d.data());
      setBudgetDocs(budgetDocsPlain);

      // 2) Classified rows for the selected range (shared engine)
      const { rows: includedRows, meta } = await loadClassifiedRows(
        db,
        user.uid,
        { start, end, confirmedOnly },
        budgetDocsPlain
      );
      setRows(includedRows);
      setMetaCounts({
        excludedNonBudget: meta.excludedNonBudget ?? 0,
        excludedNonBudgetByName: meta.excludedNonBudgetByName ?? 0,
        unmappedUsedSignFallback: 0, // handled inside reportingCore
      });

      // 3) Totals (respect current filters at the time of run)
      const filteredRows = applyCategoryFilter(includedRows);
      const drivingRows = hasAnyFilter ? filteredRows : includedRows;
      setTotals(sumTotals(drivingRows));

      // 4) Series (daily for "This Month", monthly otherwise) using shared engine
      const useDaily = preset === "thisMonth";
      const series = groupMonthly(drivingRows, { start, end, daily: useDaily });
      setMonthly(series);
      setT12(series.map(r => r.month));
    } catch (e) {
      console.error(e);
      alert(e?.message || "Failed to load reports.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (auth.currentUser) run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.currentUser]);

  /* ===== Category filter helpers ===== */
  const toggleCat = (catNorm) => {
    const next = new Set(selectedCats);
    if (next.has(catNorm)) next.delete(catNorm);
    else next.add(catNorm);
    setSelectedCats(next);
    // if turning off a category, also drop its sub selections
    if (!next.has(catNorm)) {
      const filtered = new Set(
        Array.from(selectedSubs).filter((k) => !k.startsWith(`${catNorm}::`))
      );
      setSelectedSubs(filtered);
    }
  };

  const toggleSub = (catNorm, subNorm) => {
    const key = `${catNorm}::${subNorm}`;
    const next = new Set(selectedSubs);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelectedSubs(next);
  };

  const clearAllFilters = () => {
    setSelectedCats(new Set());
    setSelectedSubs(new Set());
    setCompareMode("combine");
  };

  const applyCategoryFilter = (rowsIn) => {
    if (!hasAnyFilter) return rowsIn;

    // If any subcategories are chosen, they take precedence
    if (selectedSubs.size > 0) {
      return rowsIn.filter((tx) => {
        const c = normalizeLabel(tx.category);
        const s = normalizeLabel(tx.subcategory);
        return selectedSubs.has(`${c}::${s}`);
      });
    }

    // Otherwise, filter by selected categories (optionally include children)
    if (selectedCats.size > 0) {
      if (includeChildren) {
        return rowsIn.filter((tx) => selectedCats.has(normalizeLabel(tx.category)));
      }
      // category-only (no children): keep rows that have no subcategory label
      return rowsIn.filter((tx) => {
        const c = normalizeLabel(tx.category);
        const s = normalizeLabel(tx.subcategory);
        return selectedCats.has(c) && !s;
      });
    }

    return rowsIn;
  };

  // Filter predicate for budgets, shared by Trends and BvA.
  // Honors category/subcategory selections and Include children.
  const keepBudgetEntry = (type, category, subcategory) => {
    // ignore non-budget (defensive)
    if (String(type).toLowerCase() === "non-budget") return false;

    const cNorm = normalizeLabel(category);
    const sNorm = normalizeLabel(subcategory);

    const hasSubs = selectedSubs.size > 0;
    const hasCats = selectedCats.size > 0;

    if (!hasSubs && !hasCats) return true; // no filters

    if (hasSubs) {
      // explicit subcategory selections win
      return selectedSubs.has(`${cNorm}::${sNorm}`);
    }

    // category-only filtering
    if (hasCats) {
      if (includeChildren) return selectedCats.has(cNorm);
      // category-only (no children): only keep entries with empty subcategory
      return selectedCats.has(cNorm) && !sNorm;
    }

    return true;
  };

  function buildSelectionBreakdown(rowsIn, selectedCats, selectedSubs) {
    const out = [];
    if (selectedSubs.size > 0) {
      // subcategory selections have precedence
      const map = new Map(); // "Cat: Sub" -> { total, type }
      for (const r of rowsIn) {
        const c = normalizeLabel(r.category);
        const s = normalizeLabel(r.subcategory);
        const keyNorm = `${c}::${s}`;
        if (!selectedSubs.has(keyNorm)) continue;
        const label = r.subcategory ? `${r.category}: ${r.subcategory}` : `${r.category}: (Uncategorized)`;
        const prev = map.get(label) || { total: 0, type: r._type === "income" ? "income" : "expense" };
        prev.total += r.amount;
        // lock type: if any income shows up we call it income, else expense
        prev.type = (r._type === "income") ? "income" : prev.type;
        map.set(label, prev);
      }
      for (const [label, v] of map) out.push({ label, total: v.total, type: v.type });
    } else if (selectedCats.size > 0) {
      const map = new Map(); // Category -> { total, type }
      for (const r of rowsIn) {
        const cNorm = normalizeLabel(r.category);
        if (!selectedCats.has(cNorm)) continue;
        const prev = map.get(r.category) || { total: 0, type: r._type === "income" ? "income" : "expense" };
        prev.total += r.amount;
        prev.type = (r._type === "income") ? "income" : prev.type;
        map.set(r.category, prev);
      }
      for (const [label, v] of map) out.push({ label, total: v.total, type: v.type });
    }
    // stable sort: biggest magnitude first
    out.sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
    return out;
  }

  /* ===== CSV export (monthly) ===== */
  const handleExportCSV = () => {
    const header = ["Period", "Income", "Expenses", "Net"];
    const lines = [header.join(",")];
    monthly.forEach((r) => {
      lines.push(
        [
          r.month,
          (r.income ?? 0).toFixed(2),
          (r.expenses ?? 0).toFixed(2),
          (r.net ?? 0).toFixed(2),
        ].join(",")
      );
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const firstKey = monthly[0]?.month ?? "start";
    const lastKey = monthly[monthly.length - 1]?.month ?? "end";
    a.download = `trelli-report-${firstKey}_to_${lastKey}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /* ===== simple SVG line chart ===== */
  const svgRef = useRef(null);
  const [hoverIdx, setHoverIdx] = useState(null);
  const [barHover, setBarHover] = useState(null);
  // barHover = { x, y, type:'inc'|'exp'|'sav'|'net', month, label, value, total }
  const [pieHover, setPieHover] = useState(null);
  // pieHover = { kind:'income'|'expense'|'savings', x, y, label, value, color }

  const width = 860;
  const height = 220;
  const pad = 28;

  const netSeries = monthly.map((r) => r.net ?? 0);
  const minY = Math.min(0, ...netSeries);
  const maxY = Math.max(0, ...netSeries);
  const yPad = Math.max(1, Math.round((maxY - minY) * 0.08));
  const yMin = minY - yPad;
  const yMax = maxY + yPad;

  // Average of the series we’re showing
  const avgNet =
    monthly.length ? netSeries.reduce((a, b) => a + (b ?? 0), 0) / monthly.length : 0;

  // Simple linear regression for trend line (index-based X)
  const n = monthly.length;
  let trendSlope = 0, trendIntercept = 0;
  if (n >= 2) {
    let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
    for (let i = 0; i < n; i++) {
      const x = i, y = netSeries[i] ?? 0;
      sumX += x; sumY += y; sumXY += x * y; sumXX += x * x;
    }
    const denom = n * sumXX - sumX * sumX;
    trendSlope = denom === 0 ? 0 : (n * sumXY - sumX * sumY) / denom;
    trendIntercept = (sumY - trendSlope * sumX) / n;
  } else {
    trendSlope = 0;
    trendIntercept = avgNet;
  }
  const trendY = (i) => trendSlope * i + trendIntercept;

  const xFor = (i) => {
    const n = Math.max(1, monthly.length - 1);
    return pad + (i * (width - pad * 2)) / n;
  };
  const yFor = (v) => {
    if (yMax === yMin) return height / 2;
    return height - pad - ((v - yMin) * (height - pad * 2)) / (yMax - yMin);
  };

  // Path for the trend line
  const trendPath = n
    ? monthly.map((_, i) => `${i === 0 ? "M" : "L"} ${xFor(i)} ${yFor(trendY(i))}`).join(" ")
    : "";

  const linePath = (() => {
    if (monthly.length === 0) return "";
    return monthly
      .map((r, i) => `${i === 0 ? "M" : "L"} ${xFor(i)} ${yFor(r.net ?? 0)}`)
      .join(" ");
  })();

  // Map screen pixels -> SVG coords (precise, works with responsive width)
  const svgPoint = (evt, svgEl) => {
    const pt = svgEl.createSVGPoint();
    pt.x = evt.clientX;
    pt.y = evt.clientY;
    const ctm = svgEl.getScreenCTM();
    return ctm ? pt.matrixTransform(ctm.inverse()) : { x: 0, y: 0 };
  };

  const onMouseMove = (e) => {
    const svg = svgRef.current;
    if (!svg || monthly.length === 0) return;
    const { x } = svgPoint(e, svg);           // <- precise SVG x
    // find nearest index by distance to the real x-position of each point
    let bestI = 0, bestDX = Infinity;
    const nSpan = Math.max(1, monthly.length - 1);
    for (let i = 0; i < monthly.length; i++) {
      const xi = pad + (i * (width - pad * 2)) / nSpan;
      const dx = Math.abs(x - xi);
      if (dx < bestDX) { bestDX = dx; bestI = i; }
    }
    setHoverIdx(bestI);
  };

  const onMouseLeave = () => setHoverIdx(null);

  /* ===== insights (top categories + trends) ===== */
  // Utility: build display label for the summary card based on filters
  const summaryLabel = useMemo(() => {
    // define per-view flags inside this closure
    const onlyIncome  = totals.expenses === 0 && totals.income > 0;
    const onlyExpense = totals.income === 0 && totals.expenses < 0;

    if (!hasAnyFilter) return "Net for selected period";

    // build short label of selected filters
    const labels = [];
    if (selectedSubs.size > 0) {
      const first = Array.from(selectedSubs)[0].split("::")[1];
      labels.push(first);
      if (selectedSubs.size > 1) labels.push(`+${selectedSubs.size - 1} more`);
    } else if (selectedCats.size > 0) {
      const first = Array.from(selectedCats)[0];
      labels.push(first.replace(/(^\w)|(\s\w)/g, (m) => m.toUpperCase()));
      if (selectedCats.size > 1) labels.push(`+${selectedCats.size - 1} more`);
    }

    if (onlyIncome)  return `Total received in ${labels.join(" ") || "selected filters"}`;
    if (onlyExpense) return `Total spent in ${labels.join(" ") || "selected filters"}`;
    return "Net for selected filters";
  }, [hasAnyFilter, selectedCats, selectedSubs, totals.income, totals.expenses]);

  // Data the insights operate on = same series rows we use everywhere (driving rows)
  const drivingRows = useMemo(() => {
    const rowsToFilter = rows;
    const filtered = hasAnyFilter ? applyCategoryFilter(rowsToFilter) : rowsToFilter;
    return filtered;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, hasAnyFilter, selectedCats, selectedSubs, includeChildren]);

  // Months in scope for Budget vs Actual (always monthly buckets)
  const monthsForBVA = useMemo(() => {
    const span = monthsBetweenKeys(start, end);
    const nowKey = ymKey(new Date());
    if (preset === "thisMonth") {
      // Always include the active month when viewing “This Month”
      return [nowKey];
    }
    return bvaCompletedOnly ? span.filter((k) => k < nowKey) : span;
  }, [start, end, bvaCompletedOnly, preset]);

  const includesCurrentMonthMTD = useMemo(() => {
    const nowK = ymKey(new Date());
    if (preset === "thisMonth") return true;
    return !bvaCompletedOnly && monthsForBVA.includes(nowK);
  }, [monthsForBVA, bvaCompletedOnly, preset]);

  useEffect(() => {
    if (!user || monthsForBVA.length === 0) return;
    let cancelled = false;

    (async () => {
      const acc = new Map();
      for (const mKey of monthsForBVA) {
        try {
          const ref = doc(db, "users", user.uid, "monthlyBudgets", mKey);
          const snap = await getDoc(ref);
          if (snap.exists()) {
            const obj = snap.data()?.totalsByKey || {};
            // Convert object back to Map<string, number>
            const m = new Map(Object.entries(obj).map(([k, v]) => [k, Number(v) || 0]));
            acc.set(mKey, m);
          }
        } catch (e) {
          // ignore read errors; we'll compute live
        }
      }
      if (!cancelled) setBudgetSnaps(acc);
    })();

    return () => { cancelled = true; };
  }, [user, db, monthsForBVA.join("|")]);

  useEffect(() => {
    if (!user || budgetDocs.length === 0 || monthsForBVA.length === 0) return;

    let cancelled = false;

    (async () => {
      for (const mKey of monthsForBVA) {
        if (cancelled) break;

        // Only auto-freeze completed months
        const nowKey = ymKey(new Date());
        if (mKey >= nowKey) continue;

        // Skip if we already have a snapshot, or we already tried this session
        if (budgetSnaps.has(mKey) || snapTried.has(mKey)) continue;

        try {
          // Compute budget map for this month using the shared math
          const { y, m } = parseMonthKey(mKey);
          const map = computeBudgetForMonthList(budgetDocs, y, m);

          // Prepare totals
          let income = 0, expense = 0;
          const totalsByKey = {};
          for (const [k, v] of map.entries()) {
            totalsByKey[k] = Number(v) || 0;
            const { type } = parseKey(k);
            if (String(type).toLowerCase() === "income") income += Number(v) || 0;
            else expense += Number(v) || 0; // savings counted as outflow
          }

          // Write snapshot if it doesn't exist yet
          const ref = doc(db, "users", user.uid, "monthlyBudgets", mKey);
          const existing = await getDoc(ref);
          if (!existing.exists()) {
            await setDoc(ref, {
              userId: user.uid,
              version: 1,
              computedAt: serverTimestamp(),
              totalsByKey,
              totalsByType: { income, expense }
            });
          }

          // Mark tried and refresh in-memory snapshots for immediate use
          setSnapTried(prev => new Set(prev).add(mKey));
          setBudgetSnaps(prev => {
            const next = new Map(prev);
            if (!next.has(mKey)) next.set(mKey, new Map(Object.entries(totalsByKey)));
            return next;
          });
        } catch (e) {
          // Mark tried to avoid spinning; we'll try again on a future visit
          setSnapTried(prev => new Set(prev).add(mKey));
        }
      }
    })();

    return () => { cancelled = true; };
  }, [user, db, budgetDocs, monthsForBVA, budgetSnaps, snapTried]);

  /* ===== Budget vs Actual (by month) — live compute, snapshots next step ===== */
  const bva = useMemo(() => {
    if (!user || !budgetDocs || budgetDocs.length === 0 || monthsForBVA.length === 0) {
      return { months: [], rows: [] };
    }
    // Build rows filtered exactly like the rest of the page
    const filtered = applyCategoryFilter(rows);
    const driving = hasAnyFilter ? filtered : rows;

    return {
      months: monthsForBVA,
      rows: [],
    };
  }, [user, rows, budgetDocs, monthsForBVA, hasAnyFilter, selectedCats, selectedSubs, includeChildren]);

  // Resolve BvA rows from the shared engine (keeps UI responsive)
  const [bvaComputed, setBvaComputed] = useState({ months: [], rows: [] });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user || !budgetDocs || budgetDocs.length === 0 || monthsForBVA.length === 0) {
        setBvaComputed({ months: [], rows: [] });
        return;
      }
      const filtered = applyCategoryFilter(rows);
      const driving = hasAnyFilter ? filtered : rows;
      const out = await computeBVA({
        months: monthsForBVA,
        rows: driving,
        budgetDocs,
        filter: { selectedCats, selectedSubs, includeChildren },
        includeCurrentMonthMTD: !bvaCompletedOnly,
        db,
        uid: user.uid,
      });
      if (!cancelled) setBvaComputed(out);
    })();
    return () => { cancelled = true; };
  }, [user, rows, budgetDocs, monthsForBVA, hasAnyFilter, selectedCats, selectedSubs, includeChildren, bvaCompletedOnly, db]);

  // Build Budgets: Top Beats / Worst Misses (by category & subcategory) over the selected months
  const {
    beatCatsTop, missCatsTop, beatSubsTop, missSubsTop,
    beatCatsAll, missCatsAll, beatSubsAll, missSubsAll
  } = useMemo(() => {
    const empty = {
      beatCatsTop: [], missCatsTop: [], beatSubsTop: [], missSubsTop: [],
      beatCatsAll: [], missCatsAll: [], beatSubsAll: [], missSubsAll: [],
    };
    const months = monthsForBVA;
    if (!budgetDocs || budgetDocs.length === 0 || months.length === 0) return empty;

    // Predicate to align with current filters (same logic we use for BvA)
    const hasSubs = selectedSubs.size > 0;
    const hasCats = selectedCats.size > 0;
    const includeKids = includeChildren;
    function keepBudgetEntry(type, category, subcategory) {
      if (String(type).toLowerCase() === "non-budget") return false;
      const cNorm = normalizeLabel(category);
      const sNorm = normalizeLabel(subcategory);

      if (!hasSubs && !hasCats) return true;
      if (hasSubs) return selectedSubs.has(`${cNorm}::${sNorm}`);
      if (hasCats) {
        if (includeKids) return selectedCats.has(cNorm);
        return selectedCats.has(cNorm) && !sNorm;
      }
      return true;
    }

    const nowKey = ymKey(new Date());

    // Budget aggregates (label -> { type, b })
    const catB = new Map();
    const subB = new Map();
    const addB = (type, category, subcategory, amount) => {
      const t = String(type).toLowerCase();
      if (t === "non-budget") return;
      const catLabel = category || "(Uncategorized)";
      const subLabel = subcategory ? `${category}: ${subcategory}` : `${category}: (Uncategorized)`;
      if (!catB.has(catLabel)) catB.set(catLabel, { type: t, b: 0 });
      if (!subB.has(subLabel)) subB.set(subLabel, { type: t, b: 0 });
      catB.get(catLabel).b += Number(amount) || 0;
      subB.get(subLabel).b += Number(amount) || 0;
    };

    for (const mKey of months) {
      const isMTD = !bvaCompletedOnly && mKey === nowKey;
      if (isMTD) {
        // Current month MTD: use expected-to-date, respecting filters
        for (const b of budgetDocs) {
          const type = String(b?.type || "").toLowerCase();
          const category = b?.category || "";
          if (!category || type === "non-budget") continue;
          const subs = Array.isArray(b?.subcategories) ? b.subcategories : [];
          for (const sub of subs) {
            const name = sub?.name || "";
            if (!keepBudgetEntry(type, category, name)) continue;
            const isInc = type === "income";
            const amt = expectedToDateForSub(sub, isInc);
            addB(type, category, name, amt);
          }
        }
      } else {
        // Completed month: use snapshot or live compute map
        const mm = getBudgetMapForMonthKey(mKey, budgetSnaps, budgetDocs);
        if (mm instanceof Map) {
          for (const [k, v] of mm.entries()) {
            const { type, category, subcategory } = parseKey(k);
            if (!keepBudgetEntry(type, category, subcategory)) continue;
            addB(type, category, subcategory, Number(v) || 0);
          }
        }
      }
    }

    // Actuals for the same months (from drivingRows)
    const monthsSet = new Set(months);
    const catA = new Map();
    const subA = new Map();
    const addA = (map, label, val) => map.set(label, (map.get(label) ?? 0) + (Number(val) || 0));

    for (const r of drivingRows) {
      const mk = ymKey(new Date(r.date.getFullYear(), r.date.getMonth(), 1));
      if (!monthsSet.has(mk)) continue;
      const catLabel = r.category || "(Uncategorized)";
      const subLabel = r.subcategory ? `${r.category}: ${r.subcategory}` : `${r.category}: (Uncategorized)`;
      if (r._type === "income") {
        addA(catA, catLabel, r.amount);
        addA(subA, subLabel, r.amount);
      } else {
        const spend = -r.amount; // positive magnitude
        addA(catA, catLabel, spend);
        addA(subA, subLabel, spend);
      }
    }

    function buildBeatMiss(bMap, aMap) {
      const beats = [];
      const misses = [];
      for (const [label, info] of bMap.entries()) {
        const b = info.b || 0;
        if (b <= 0) continue;            // ignore items with no budget
        const t = String(info.type);
        const a = aMap.get(label) ?? 0;

        if (t === "income") {
          const beatAmt = a - b; if (beatAmt > 0) beats.push({ label, amount: beatAmt });
          const missAmt = b - a; if (missAmt > 0) misses.push({ label, amount: missAmt });
        } else {
          // expense/savings: under is good, over is bad
          const beatAmt = b - a; if (beatAmt > 0) beats.push({ label, amount: beatAmt });
          const missAmt = a - b; if (missAmt > 0) misses.push({ label, amount: missAmt });
        }
      }
      beats.sort((a,b) => b.amount - a.amount);
      misses.sort((a,b) => b.amount - a.amount);
      return {
        beatAll: beats,
        missAll: misses,
        beatTop: beats.slice(0, 3),
        missTop: misses.slice(0, 3),
      };
    }

    const cats = buildBeatMiss(catB, catA);
    const subs = buildBeatMiss(subB, subA);

    return {
      beatCatsTop: cats.beatTop, missCatsTop: cats.missTop,
      beatSubsTop: subs.beatTop, missSubsTop: subs.missTop,
      beatCatsAll: cats.beatAll, missCatsAll: cats.missAll,
      beatSubsAll: subs.beatAll, missSubsAll: subs.missAll,
    };
  }, [budgetDocs, budgetSnaps, monthsForBVA, drivingRows, selectedCats, selectedSubs, includeChildren, bvaCompletedOnly]);

  // Budget vs Actual summary by CATEGORY across the BvA months in view
  const { beatBudgets, missedBudgets } = useMemo(() => {
    if (!budgetDocs || budgetDocs.length === 0 || monthsForBVA.length === 0) {
      return { beatBudgets: [], missedBudgets: [] };
    }

    // Copy of the filter predicate used in BvA (same semantics)
    const hasSubs = selectedSubs.size > 0;
    const hasCats = selectedCats.size > 0;
    const includeKids = includeChildren;
    const keepBudgetEntry = (type, category, subcategory) => {
      if (String(type).toLowerCase() === "non-budget") return false;
      const cNorm = normalizeLabel(category);
      const sNorm = normalizeLabel(subcategory);
      if (!hasSubs && !hasCats) return true;
      if (hasSubs) return selectedSubs.has(`${cNorm}::${sNorm}`);
      if (hasCats) {
        if (includeKids) return selectedCats.has(cNorm);
        return selectedCats.has(cNorm) && !sNorm;
      }
      return true;
    };

    // 1) Sum BUDGET by category across months
    const bByCat = new Map(); // cat -> {inc, exp}
    for (const mKey of monthsForBVA) {
      const mm = getBudgetMapForMonthKey(mKey, budgetSnaps, budgetDocs);
      if (!(mm instanceof Map)) continue;
      for (const [k, v] of mm.entries()) {
        const { type, category, subcategory } = parseKey(k);
        if (!keepBudgetEntry(type, category, subcategory)) continue;
        const t = String(type).toLowerCase();
        const cur = bByCat.get(category) || { inc: 0, exp: 0 };
        if (t === "income") cur.inc += Number(v) || 0;
        else cur.exp += Number(v) || 0; // expenses + savings are outflows
        bByCat.set(category, cur);
      }
    }

    // 2) Sum ACTUAL by category across the same months (from drivingRows)
    const aByCat = new Map(); // cat -> {inc, exp}
    for (const r of drivingRows) {
      const mk = `${r.date.getFullYear()}-${String(r.date.getMonth()+1).padStart(2,"0")}`;
      if (!monthsForBVA.includes(mk)) continue;
      const cat = r.category || "(Uncategorized)";
      const cur = aByCat.get(cat) || { inc: 0, exp: 0 };
      if (r._type === "income") cur.inc += r.amount;
      else cur.exp += -r.amount; // spend magnitude
      aByCat.set(cat, cur);
    }

    // 3) Variance per BvA rules (favorable = green)
    const onlyIncome  = totals.expenses === 0 && totals.income > 0;
    const onlyExpense = totals.income === 0 && totals.expenses < 0;

    const rows = [];
    for (const cat of new Set([...bByCat.keys(), ...aByCat.keys()])) {
      const b = bByCat.get(cat) || { inc: 0, exp: 0 };
      const a = aByCat.get(cat) || { inc: 0, exp: 0 };
      let variance = 0;
      if (onlyIncome) variance = a.inc - b.inc;
      else if (onlyExpense) variance = b.exp - a.exp;
      else variance = (a.inc - a.exp) - (b.inc - b.exp);
      rows.push({ label: cat, variance });
    }

    const beat   = rows.filter(r => r.variance >= 0).sort((a,b)=> b.variance - a.variance).slice(0,3);
    const missed = rows.filter(r => r.variance <  0).sort((a,b)=> a.variance - b.variance).slice(0,3);

    return { beatBudgets: beat, missedBudgets: missed };
  }, [budgetDocs, budgetSnaps, monthsForBVA, drivingRows, selectedCats, selectedSubs, includeChildren, totals.income, totals.expenses]);

  const {
    incomeCatsAll, expenseCatsAll,
    incomeSubsAll, expenseSubsAll
  } = useMemo(() => {
    const incomeByCat = new Map();
    const expenseByCat = new Map();
    const incomeBySub = new Map();
    const expenseBySub = new Map();

    for (const r of drivingRows) {
      const cat = r.category || "(Uncategorized)";
      const sub = r.subcategory || "";
      const subLabel = sub ? `${cat}: ${sub}` : `${cat}: (Uncategorized)`;

      if (r._type === "income") {
        // income totals (positive)
        incomeByCat.set(cat, (incomeByCat.get(cat) ?? 0) + r.amount);
        incomeBySub.set(subLabel, (incomeBySub.get(subLabel) ?? 0) + r.amount);
      } else {
        // expense/savings as positive magnitudes
        const spend = -r.amount;
        if (spend <= 0) continue;
        expenseByCat.set(cat, (expenseByCat.get(cat) ?? 0) + spend);
        expenseBySub.set(subLabel, (expenseBySub.get(subLabel) ?? 0) + spend);
      }
    }

    const sortAll = (m) =>
      Array.from(m.entries())
        .map(([label, value]) => ({ label, value }))
        .sort((a, b) => b.value - a.value);

    return {
      incomeCatsAll: sortAll(incomeByCat),
      expenseCatsAll: sortAll(expenseByCat),
      incomeSubsAll: sortAll(incomeBySub),
      expenseSubsAll: sortAll(expenseBySub),
    };
  }, [drivingRows]);

  const compareSeries = useMemo(() => {
    if (compareMode !== "compare" || selectedCount < 2 || monthly.length === 0) return [];
    // Build a fast lookup per period key of all rows
    const keyForRow = (r) => (
      preset === "thisMonth"
        ? `${r.date.getFullYear()}-${String(r.date.getMonth() + 1).padStart(2, "0")}-${String(r.date.getDate()).padStart(2, "0")}`
        : ymKey(new Date(r.date.getFullYear(), r.date.getMonth(), 1))
    );

    // Collect selections
    const selections = selectedSubs.size > 0
      ? Array.from(selectedSubs).map((norm) => {
          const [cNorm, sNorm] = norm.split("::");
          return { kind: "sub", norm, cNorm, sNorm };
        })
      : Array.from(selectedCats).map((cNorm) => ({ kind: "cat", cNorm }));

    // Build label & series per selection
    const series = selections.map((sel) => {
      const label = sel.kind === "sub"
        ? (() => {
            // find any row to fetch original labels
            const found = drivingRows.find((r) =>
              normalizeLabel(r.category) === sel.cNorm &&
              normalizeLabel(r.subcategory) === sel.sNorm
            );
            return found ? `${found.category}: ${found.subcategory || "(Uncategorized)"}` : `${sel.cNorm}:${sel.sNorm}`;
          })()
        : (() => {
            const found = drivingRows.find((r) => normalizeLabel(r.category) === sel.cNorm);
            return found ? found.category : sel.cNorm;
          })();

      // per-key sums (income positive, expense negative)
      const arr = periodKeys.map((k) => 0);
      for (const r of drivingRows) {
        if (sel.kind === "sub") {
          if (normalizeLabel(r.category) !== sel.cNorm || normalizeLabel(r.subcategory) !== sel.sNorm) continue;
        } else {
          if (normalizeLabel(r.category) !== sel.cNorm) continue;
        }
        const k = keyForRow(r);
        const idx = periodKeys.indexOf(k);
        if (idx >= 0) arr[idx] += r.amount;
      }
      return { label, values: arr };
    });

    return series;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareMode, selectedCats, selectedSubs, drivingRows, periodKeys, preset, monthly.length, selectedCount]);

  // Top categories (income & expense) for PERIOD TOTALS
  const topIncome = useMemo(() => {
    const m = new Map();
    for (const r of drivingRows) {
      if (r._type !== "income") continue;
      const k = r.category || "(Uncategorized)";
      m.set(k, (m.get(k) ?? 0) + r.amount);
    }
    return Array.from(m.entries())
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }, [drivingRows]);

  const topExpense = useMemo(() => {
    const m = new Map();
    for (const r of drivingRows) {
      if (r._type === "income") continue;
      const k = r.category || "(Uncategorized)";
      const spend = -r.amount; // make positive
      if (spend <= 0) continue;
      m.set(k, (m.get(k) ?? 0) + spend);
    }
    return Array.from(m.entries())
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }, [drivingRows]);

  // one-badge trend (good / bad / none) across the current series
  const trendBadge = useMemo(() => {
    if (monthly.length < 2) return { kind: "none" };

    // choose which series to judge
    const onlyIncome = totals.expenses === 0 && totals.income > 0;
    const onlyExpense = totals.income === 0 && totals.expenses < 0;

    const series = onlyIncome
      ? monthly.map((m) => m.income ?? 0)
      : onlyExpense
      ? monthly.map((m) => -(m.expenses ?? 0)) // spend magnitude (positive)
      : monthly.map((m) => m.net ?? 0);

    // slope via simple regression
    const n = series.length;
    let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
    for (let i = 0; i < n; i++) {
      sumX += i; sumY += series[i]; sumXY += i * series[i]; sumXX += i * i;
    }
    const denom = n * sumXX - sumX * sumX;
    const slope = denom === 0 ? 0 : (n * sumXY - sumX * sumY) / denom;

    const per = (preset === "thisMonth") ? "day" : "month";
    const epsilon = 0.0001;

    if (onlyExpense) {
      if (slope < -epsilon) return { kind: "good", emoji: "🏆", text: `Improving — spending down ~${toUSD(-slope)} per ${per}` };
      if (slope >  epsilon) return { kind: "bad",  emoji: "⚠", text: `Trending up — spending up ~${toUSD(slope)} per ${per}` };
      return { kind: "none", emoji: "·", text: "No clear trend" };
    } else if (onlyIncome) {
      if (slope >  epsilon) return { kind: "good", emoji: "🏆", text: `Improving — income up ~${toUSD(slope)} per ${per}` };
      if (slope < -epsilon) return { kind: "bad",  emoji: "⚠", text: `Trending down — income down ~${toUSD(-slope)} per ${per}` };
      return { kind: "none", emoji: "·", text: "No clear trend" };
    } else {
      if (slope >  epsilon) return { kind: "good", emoji: "🏆", text: `Improving — net up ~${toUSD(slope)} per ${per}` };
      if (slope < -epsilon) return { kind: "bad",  emoji: "⚠", text: `Trending down — net down ~${toUSD(-slope)} per ${per}` };
      return { kind: "none", emoji: "·", text: "No clear trend" };
    }
  }, [monthly, totals.income, totals.expenses, preset]);

  // Trends per category across the same keys we chart (daily for this month, monthly otherwise)
  function trendFor(values) {
    // simple linear regression slope
    const n = values.length;
    if (n < 2) return 0;
    let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
    for (let i = 0; i < n; i++) {
      const x = i, y = values[i] ?? 0;
      sumX += x; sumY += y; sumXY += x * y; sumXX += x * x;
    }
    const denom = n * sumXX - sumX * sumX;
    return denom === 0 ? 0 : (n * sumXY - sumX * sumY) / denom;
  }

  // We want trends for categories *and* subcategories, plus full lists we can show at the bottom
  const perUnit = preset === "thisMonth" ? "day" : "month";
  const fmtSlope = (s) => `${s >= 0 ? "↑" : "↓"} ${toUSD(Math.abs(s))} / ${perUnit}`;

  const {
    catUpTop, catDownTop, subUpTop, subDownTop,
    catUpAll, catDownAll, subUpAll, subDownAll
  } = useMemo(() => {
    // Build per-key series for categories and subcategories using the SAME period keys the chart uses
    const keys = periodKeys;

    const catKeySums = new Map();   // "Category" -> Map(periodKey -> signed sum)
    const subKeySums = new Map();   // "Category: Sub" -> Map(periodKey -> signed sum)

    for (const r of drivingRows) {
      const key = (preset === "thisMonth")
        ? `${r.date.getFullYear()}-${String(r.date.getMonth() + 1).padStart(2, "0")}-${String(r.date.getDate()).padStart(2, "0")}`
        : ymKey(new Date(r.date.getFullYear(), r.date.getMonth(), 1));

      const val = (r._type === "income") ? r.amount : -r.amount; // signed: income +, expense -

      const cat = r.category || "(Uncategorized)";
      if (!catKeySums.has(cat)) catKeySums.set(cat, new Map());
      catKeySums.get(cat).set(key, (catKeySums.get(cat).get(key) ?? 0) + val);

      const subLabel = r.subcategory ? `${r.category}: ${r.subcategory}` : `${r.category}: (Uncategorized)`;
      if (!subKeySums.has(subLabel)) subKeySums.set(subLabel, new Map());
      subKeySums.get(subLabel).set(key, (subKeySums.get(subLabel).get(key) ?? 0) + val);
    }

    function buildLists(map) {
      const rows = [];
      for (const [label, perKey] of map.entries()) {
        const values = keys.map((k) => perKey.get(k) ?? 0);
        const slope = trendFor(values);
        if (Math.abs(slope) < 1e-9) continue; // ignore flat lines
        rows.push({ label, slope });
      }
      const upAll = rows.filter(r => r.slope > 0).sort((a,b) => b.slope - a.slope);
      const downAll = rows.filter(r => r.slope < 0).sort((a,b) => a.slope - b.slope);
      return {
        upAll,
        downAll,
        upTop: upAll.slice(0, 3),
        downTop: downAll.slice(0, 3),
      };
    }

    const cats = buildLists(catKeySums);
    const subs = buildLists(subKeySums);

    return {
      catUpTop:  cats.upTop,  catDownTop:  cats.downTop,
      subUpTop:  subs.upTop,  subDownTop:  subs.downTop,
      catUpAll:  cats.upAll,  catDownAll:  cats.downAll,
      subUpAll:  subs.upAll,  subDownAll:  subs.downAll,
    };
  }, [drivingRows, periodKeys, preset]);

  // Derive FAVORABLE vs NEEDS-ATTENTION from the up/down lists + group type
  const {
    catsImprovingTop, catsWorseningTop,
    subsImprovingTop, subsWorseningTop
  } = useMemo(() => {
    const catUpInc   = catUpTop.filter(x  => inferGroupTypeFromRows(x.label, false, drivingRows) === "income");
    const catUpExp   = catUpTop.filter(x  => inferGroupTypeFromRows(x.label, false, drivingRows) === "expense");
    const catDownInc = catDownTop.filter(x=> inferGroupTypeFromRows(x.label, false, drivingRows) === "income");
    const catDownExp = catDownTop.filter(x=> inferGroupTypeFromRows(x.label, false, drivingRows) === "expense");

    const subUpInc   = subUpTop.filter(x  => inferGroupTypeFromRows(x.label, true, drivingRows) === "income");
    const subUpExp   = subUpTop.filter(x  => inferGroupTypeFromRows(x.label, true, drivingRows) === "expense");
    const subDownInc = subDownTop.filter(x=> inferGroupTypeFromRows(x.label, true, drivingRows) === "income");
    const subDownExp = subDownTop.filter(x=> inferGroupTypeFromRows(x.label, true, drivingRows) === "expense");

    // Favorable = income trending UP  +  expenses trending DOWN
    const catsImproving   = [...catUpInc, ...catDownExp].sort((a,b)=>Math.abs(b.slope)-Math.abs(a.slope)).slice(0,3);
    const subsImproving   = [...subUpInc, ...subDownExp].sort((a,b)=>Math.abs(b.slope)-Math.abs(a.slope)).slice(0,3);

    // Needs attention = income trending DOWN  +  expenses trending UP
    const catsWorsening   = [...catDownInc, ...catUpExp].sort((a,b)=>Math.abs(b.slope)-Math.abs(a.slope)).slice(0,3);
    const subsWorsening   = [...subDownInc, ...subUpExp].sort((a,b)=>Math.abs(b.slope)-Math.abs(a.slope)).slice(0,3);

    return {
      catsImprovingTop: catsImproving,
      catsWorseningTop: catsWorsening,
      subsImprovingTop: subsImproving,
      subsWorseningTop: subsWorsening
    };
  }, [catUpTop, catDownTop, subUpTop, subDownTop, drivingRows]);

  const {
    fullCatsImproving, fullCatsWorsening,
    fullSubsImproving, fullSubsWorsening
  } = useMemo(() => {
    // helper to split into favorable/worsening for a list of {label, slope}
    const split = (list, isSub) => {
      const favorable = [];
      const worsening = [];
      for (const item of list) {
        const kind = inferGroupTypeFromRows(item.label, isSub, drivingRows); // "income" | "expense"
        // Favorable = income trending UP, expenses trending DOWN
        const isFavorable =
          (kind === "income"  && item.slope > 0) ||
          (kind === "expense" && item.slope < 0);
        (isFavorable ? favorable : worsening).push(item);
      }
      // sort by absolute movement (bigger first)
      favorable.sort((a,b) => Math.abs(b.slope) - Math.abs(a.slope));
      worsening.sort((a,b) => Math.abs(b.slope) - Math.abs(a.slope));
      return { favorable, worsening };
    };

    const catsUp   = split(catUpAll, false);
    const catsDown = split(catDownAll, false);
    const subsUp   = split(subUpAll, true);
    const subsDown = split(subDownAll, true);

    return {
      fullCatsImproving:   [...catsUp.favorable,   ...catsDown.favorable],
      fullCatsWorsening:   [...catsUp.worsening,   ...catsDown.worsening],
      fullSubsImproving:   [...subsUp.favorable,   ...subsDown.favorable],
      fullSubsWorsening:   [...subsUp.worsening,   ...subsDown.worsening],
    };
  }, [catUpAll, catDownAll, subUpAll, subDownAll, drivingRows]);

  // Build a flat list of goals from budgets (type: "savings" and sub has a goal map)
  const goals = useMemo(() => {
    const list = [];
    for (const doc of budgetDocs) {
      const b = doc;
      if (String(b?.type).toLowerCase() !== "savings") continue;
      const category = b?.category || "Goals";
      const subs = Array.isArray(b?.subcategories) ? b.subcategories : [];
      for (const s of subs) {
        const g = s?.goal;
        const name = (g?.name || s?.name || "").trim();
        if (!name) continue;
        list.push({
          category,
          name,
          nameNorm: normalizeLabel(name),
          targetAmount: num(g?.targetAmount),
          targetDate: String(g?.targetDate || ""),   // "YYYY-MM"
          currentAmount: num(g?.currentAmount),      // used for "cumulative"
        });
      }
    }
    return list;
  }, [budgetDocs]);

  // Months for the tiny goal trend (prefer completed months; fallback to selected monthly span)
  const goalTrendMonths = useMemo(() => {
    const m = monthsForBVA.length ? monthsForBVA : monthsBetweenKeys(start, end);
    return m.slice(-12); // keep last up to 12
  }, [monthsForBVA, start, end]);

  // Pick the “this period” month key (last key in selected monthly span)
  const thisPeriodKey = useMemo(() => {
    const m = monthsBetweenKeys(start, end);
    return m.length ? m[m.length - 1] : ymKey(new Date());
  }, [start, end]);

  // Per-goal data for tiles: initial budget/mo, avg saved/mo, need/mo, tiny cumulative chart, ETA
  const goalsData = useMemo(() => {
    if (goals.length === 0) return [];

    // The selected monthly span drives everything in this Goals section
    const selMonths = monthsBetweenKeys(start, end);
    if (selMonths.length === 0) return [];

    const firstKey = selMonths[0];

    // Build skeleton per goal (per-month series for the selected span)
    const perGoal = new Map(); // nameNorm -> { seriesByMonth: Map<YYYY-MM, number> }
    for (const g of goals) {
      perGoal.set(g.nameNorm, { seriesByMonth: new Map(selMonths.map((k) => [k, 0])) });
    }

    // Aggregate from ALL included rows (rows = after status + non-budget filters), regardless of category filters
    for (const r of rows) {
      if (r._type !== "savings") continue; // only savings/goal contributions
      const key = ymKey(new Date(r.date.getFullYear(), r.date.getMonth(), 1));
      if (!selMonths.includes(key)) continue; // only within selected range
      const subNorm = normalizeLabel(r.subcategory);
      const bucket = perGoal.get(subNorm);
      if (!bucket) continue; // not a named goal
      const val = -r.amount; // savings are negative outflows; use magnitude
      bucket.seriesByMonth.set(key, (bucket.seriesByMonth.get(key) ?? 0) + val);
    }

    const now = new Date();
    const y0 = now.getFullYear();
    const m0 = now.getMonth();

    return goals.map((g) => {
      // 1) Need to Save / mo (your linear rule)
      let requiredMonthly = 0;
      if (g.targetAmount && g.targetDate) {
        const { y, m } = parseMonthKey(g.targetDate);
        const monthsRemaining = monthsDiffInclusive(y0, m0, y, m); // inclusive
        const remaining = Math.max(0, (g.targetAmount || 0) - (g.currentAmount || 0)); // subtract starting
        requiredMonthly = monthsRemaining > 0 ? (remaining / monthsRemaining) : remaining;
      }

      // 2) Initial Budget / mo — read from the first month of the selected span
      let initialBudgetMonthly = 0;
      try {
        const mm = getBudgetMapForMonthKey(firstKey, budgetSnaps, budgetDocs); // Map<key, amount>
        if (mm instanceof Map) {
          for (const [k, v] of mm.entries()) {
            const { type, category, subcategory } = parseKey(k);
            if (String(type).toLowerCase() !== "savings") continue;
            if (normalizeLabel(category) !== normalizeLabel(g.category)) continue;
            if (normalizeLabel(subcategory) !== g.nameNorm) continue;
            initialBudgetMonthly += Number(v) || 0;
          }
        }
      } catch (e) {
        // ignore budget read errors; fallback 0
      }

      // 3) Avg Saved / mo over selected span
      const seriesMap = perGoal.get(g.nameNorm)?.seriesByMonth ?? new Map();
      const series = selMonths.map((k) => seriesMap.get(k) ?? 0); // magnitudes per month in range
      const sumSeries = series.reduce((a, b) => a + b, 0);
      const avgSavedMonthly = selMonths.length ? sumSeries / selMonths.length : 0;

      // 4) Cumulative planned vs actual for the tiny chart
      const baseline = g.currentAmount || 0; // treat as amount saved at start of period
      const plannedCum = selMonths.map((_, i) => baseline + requiredMonthly * (i + 1));
      const actualCum = (() => {
        let acc = baseline;
        return selMonths.map((_, i) => {
          acc += series[i] || 0;
          return acc;
        });
      })();

      // 5) ETA at current pace
      const remainingTotal = Math.max(0, (g.targetAmount || 0) - (g.currentAmount || 0));
      let etaText = "No pace yet";
      if (avgSavedMonthly > 0) {
        const monthsToHit = Math.ceil(remainingTotal / avgSavedMonthly);
        const projected = addMonths(now, monthsToHit);
        const { y: ty, m: tm } = g.targetDate ? parseMonthKey(g.targetDate) : { y: projected.getFullYear(), m: projected.getMonth() };
        const target = new Date(ty, tm, 1);
        const delta = (projected.getFullYear() - target.getFullYear()) * 12 + (projected.getMonth() - target.getMonth());
        if (delta < -1) {
          etaText = `At your ${toUSD(avgSavedMonthly)}/mo pace you’ll hit ~${fmtMonthYear(projected)} — about ${Math.abs(delta)} months before your target (${fmtMonthYear(target)}).`;
        } else if (delta > 1) {
          etaText = `At your ${toUSD(avgSavedMonthly)}/mo pace you’ll hit ~${fmtMonthYear(projected)} — about ${delta} months after your target (${fmtMonthYear(target)}).`;
        } else {
          etaText = `At your ${toUSD(avgSavedMonthly)}/mo pace you’re roughly on time (~${fmtMonthYear(projected)} vs target ${fmtMonthYear(target)}).`;
        }
      }

      return {
        goal: g,
        months: selMonths,
        series, // monthly magnitudes (for reference)
        initialBudgetMonthly,
        avgSavedMonthly,
        requiredMonthly,
        plannedCum,
        actualCum,
        etaText,
      };
    });
  }, [goals, rows, start, end, budgetSnaps, budgetDocs]);

  // Only show the Goals section when it's relevant:
  // - If no filters: show when we have any goals
  // - If filters are applied: show only if filtered rows include savings/goal tx
  const showGoals = useMemo(() => {
    if (!goalsData.length) return false;         // no goals at all
    if (!hasAnyFilter) return true;              // no filters → show goals
    // Filters applied → show only if the filtered data contains savings
    return drivingRows.some((r) => r._type === "savings");
  }, [goalsData.length, hasAnyFilter, drivingRows]);

  /* ===== styles ===== */
  const thStyle = { padding: "0.75rem", borderBottom: "1px solid #eee", fontSize: 12, color: "#666" };
  const tdStyle = { padding: "0.75rem", borderBottom: "1px solid #f2f2f2", fontSize: 14 };
  const pill = (active) => ({
    padding: "0.35rem 0.6rem",
    borderRadius: 999,
    border: "1px solid #e2e2e2",
    background: active ? "#00B074" : "white",
    color: active ? "white" : "#111",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  });

  /* ===== render ===== */
  // Roll up Budget vs Actual across the selected months (summary for the period)
  const bvaSummary = useMemo(() => {
    const onlyIncome  = totals.expenses === 0 && totals.income > 0;
    const onlyExpense = totals.income === 0 && totals.expenses < 0;

    let bInc = 0, bExp = 0, bNet = 0;
    let aInc = 0, aExp = 0, aNet = 0;

    for (const r of bvaComputed.rows) {
      bInc += Number(r.bIncome || 0);
      bExp += Number(r.bExpense || 0);
      bNet += Number(r.bNet || 0);
      aInc += Number(r.aIncome || 0);
      aExp += Number(r.aExpense || 0);
      aNet += Number(r.aNet || 0);
    }

    let variance = 0;
    if (onlyIncome)       variance = aInc - bInc;        // positive favorable
    else if (onlyExpense) variance = bExp - aExp;        // positive favorable (under budget spend)
    else                  variance = aNet - bNet;        // positive favorable

    return { bInc, bExp, bNet, aInc, aExp, aNet, variance, onlyIncome, onlyExpense };
  }, [bvaComputed.rows, totals.income, totals.expenses]);

  // Savings/Goals totals for the same BvA window (Budget & Actual) — filters respected via drivingRows
  const bvaSavings = useMemo(() => {
    if (!budgetDocs || monthsForBVA.length === 0) return { bSav: 0, aSav: 0 };
    const nowKey = ymKey(new Date());
    // Budgeted savings across months (snapshot for completed, expected-to-date for current when applicable)
    let bSav = 0;
    for (const mKey of monthsForBVA) {
      const isMTD = !bvaCompletedOnly && mKey === nowKey;
      if (isMTD) {
        // sum expectedToDate for all savings subs this month (respect filters)
        for (const b of budgetDocs) {
          const type = String(b?.type || "").toLowerCase();
          if (type !== "savings") continue;
          const category = b?.category || "";
          const subs = Array.isArray(b?.subcategories) ? b.subcategories : [];
          for (const s of subs) {
            if (!keepBudgetEntry("savings", category, s?.name || "")) continue;
            bSav += Number(expectedToDateForSub(s, false) || 0);
          }
        }
      } else {
        const mm = getBudgetMapForMonthKey(mKey, budgetSnaps, budgetDocs);
        if (mm instanceof Map) {
          for (const [k, v] of mm.entries()) {
            const { type, category, subcategory } = parseKey(k);
            if (String(type).toLowerCase() !== "savings") continue;
            if (!keepBudgetEntry(type, category, subcategory)) continue;
            bSav += Number(v) || 0;
          }
        }
      }
    }
    // Actual savings = sum magnitudes of _type === "savings" rows in the same months
    const monthsSet = new Set(monthsForBVA);
    let aSav = 0;
    for (const r of drivingRows) {
      if (r._type !== "savings") continue;
      const mk = ymKey(new Date(r.date.getFullYear(), r.date.getMonth(), 1));
      if (!monthsSet.has(mk)) continue;
      aSav += Math.max(0, -Number(r.amount || 0)); // savings spend magnitude
    }
    return { bSav, aSav };
  }, [budgetDocs, budgetSnaps, monthsForBVA, bvaCompletedOnly, drivingRows, keepBudgetEntry]);

  // Expenses excluding Savings (for display only)
  const expNoSav = {
    b: Math.max(0, (bvaSummary.bExp ?? 0) - (bvaSavings.bSav ?? 0)),
    a: Math.max(0, (bvaSummary.aExp ?? 0) - (bvaSavings.aSav ?? 0)),
  };
  
  return (
    <div style={{ padding: "1.5rem", paddingTop: "2.5rem", maxWidth: "100%", margin: 0, marginLeft: "-5px" }}>
      <h2 style={{ margin: 0, marginBottom: "0.5rem" }}>Reports</h2>
      <div style={{ color: "#555", marginBottom: "1rem" }}>
      </div>

      {/* Filters */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
          gap: "0.75rem",
          alignItems: "end",
          marginBottom: "1rem",
          background: "white",
          border: "1px solid #e5e5e5",
          borderRadius: 12,
          padding: "0.75rem",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        }}
      >
        {/* Row 1: Start | End | Run */}
        <div style={{ gridColumn: "span 4", minWidth: 240 }}>
          <label style={{ display: "block", fontSize: 12, color: "#666", marginBottom: 6 }}>
            Start Date
          </label>
          <input
            type="date"
            value={fmtInput(start)}
            onChange={(e) => {
              const v = e.target.value;
              if (!v) { setStart(null); setPreset(null); return; }
              const dt = new Date(v);
              setStart(isValidDate(dt) ? dt : null);
              setPreset(null);
            }}
            style={{ width: "100%", padding: "0.5rem", borderRadius: 8, border: "1px solid #ccc", boxSizing: "border-box" }}
          />
        </div>
        <div style={{ gridColumn: "span 4", minWidth: 240 }}>
          <label style={{ display: "block", fontSize: 12, color: "#666", marginBottom: 6 }}>
            End Date
          </label>
          <input
            type="date"
            value={fmtInput(end)}
            onChange={(e) => {
              const v = e.target.value;
              if (!v) { setEnd(null); setPreset(null); return; }
              const dt = new Date(v);
              setEnd(isValidDate(dt) ? dt : null);
              setPreset(null);
            }}
            style={{ width: "100%", padding: "0.5rem", borderRadius: 8, border: "1px solid #ccc", boxSizing: "border-box" }}
          />
        </div>
        <div style={{ gridColumn: "span 4", justifySelf: "end" }}>
          <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
            <button
              onClick={handleExportCSV}
              disabled={monthly.length === 0}
              style={{
                padding: "0.75rem",
                borderRadius: 10,
                border: "1px solid #e2e2e2",
                background: monthly.length ? "#F4C542" : "#f2f2f2",
                color: "#111",
                fontWeight: 600,
                cursor: monthly.length ? "pointer" : "default",
              }}
              title="Export the monthly/daily table as CSV"
            >
              Export CSV
            </button>

            <button
              onClick={run}
              disabled={!canRun || loading}
              style={{
                padding: "0.75rem",
                borderRadius: 10,
                border: "none",
                background: loading ? "#999" : "#00B074",
                color: "white",
                fontWeight: 600,
                cursor: loading ? "default" : "pointer",
              }}
            >
              {loading ? "Running…" : "Run"}
            </button>
          </div>
        </div>

        {/* Row 2: Presets + Completed-months toggle */}
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={{ display: "block", fontSize: 12, color: "#666", marginBottom: 6 }}>
            Presets
          </label>

          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
              <button onClick={() => applyPreset("thisMonth")} style={pill(preset === "thisMonth")}>
                This Month
              </button>
              <button onClick={() => applyPreset("last3", bvaCompletedOnly)} style={pill(preset === "last3")}>
                Last 3 Mo
              </button>
              <button onClick={() => applyPreset("ytd", bvaCompletedOnly)} style={pill(preset === "ytd")}>
                YTD
              </button>
            </div>

            {/* right side: global completed-months toggle */}
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#666" }}>
                <input
                  type="checkbox"
                  checked={bvaCompletedOnly}
                  onChange={(e) => {
                    const next = e.target.checked;
                    setBvaCompletedOnly(next);
                    if (preset === "last3" || preset === "ytd") {
                      applyPreset(preset, next);
                    }
                  }}
                />
                Completed months only
              </label>
            </div>
          </div>
        </div>

        {/* Row 3: Category/Subcategory filter */}
        <div style={{ gridColumn: "1 / -1" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              fontSize: 12,
              color: "#444",
              marginBottom: showCatPanel ? 8 : 0,
            }}
          >
            <button
              onClick={() => setShowCatPanel((v) => !v)}
              style={{
                padding: "0.4rem 0.65rem",
                border: "1px solid #e2e2e2",
                borderRadius: 999,
                background: showCatPanel ? "#00B074" : "white",
                color: showCatPanel ? "white" : "#111",
                fontWeight: 600,
                cursor: "pointer",
                fontSize: 12,
              }}
            >
              {showCatPanel ? "Hide category filters" : "Filter by category/subcategory"}
            </button>

            {hasAnyFilter && (
              <button
                onClick={clearAllFilters}
                style={{
                  padding: "0.4rem 0.65rem",
                  border: "1px solid #e2e2e2",
                  borderRadius: 999,
                  background: "white",
                  color: "#111",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontSize: 12,
                }}
                title="Clear all selected categories and subcategories"
              >
                Clear all filters
              </button>
            )}

            {hasAnyFilter && (
              <span style={{ color: "#666" }}>
                Selected: {selectedSubs.size > 0 ? `${selectedSubs.size} sub` : `${selectedCats.size} cat`}
                {selectedCats.size > 1 || selectedSubs.size > 1 ? "s" : ""}
              </span>
            )}

            {/* right-aligned controls */}
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#666" }}>
                <input
                  type="checkbox"
                  checked={confirmedOnly}
                  onChange={(e) => setConfirmedOnly(e.target.checked)}
                />
                Confirmed only
              </label>

              {/* Combine / Compare only when multiple selections */}
              {selectedCount > 1 && (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 12, color: "#666" }}>Multiple selected:</span>
                  <button
                    onClick={() => setCompareMode("combine")}
                    style={{
                      padding: "0.35rem 0.6rem",
                      borderRadius: 999,
                      border: "1px solid #e2e2e2",
                      background: compareMode === "combine" ? "#00B074" : "white",
                      color: compareMode === "combine" ? "white" : "#111",
                      fontSize: 12, fontWeight: 600, cursor: "pointer",
                    }}
                  >
                    Combine
                  </button>
                  <button
                    onClick={() => setCompareMode("compare")}
                    style={{
                      padding: "0.35rem 0.6rem",
                      borderRadius: 999,
                      border: "1px solid #e2e2e2",
                      background: compareMode === "compare" ? "#00B074" : "white",
                      color: compareMode === "compare" ? "white" : "#111",
                      fontSize: 12, fontWeight: 600, cursor: "pointer",
                    }}
                    title="Show a separate line for each selection and hide Avg/Trend"
                  >
                    Compare
                  </button>
                </div>
              )}
            </div>
          </div>

          {showCatPanel && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 2fr",
                gap: 16,
                border: "1px dashed #e5e5e5",
                borderRadius: 10,
                padding: 12,
                background: "#fcfcfc",
              }}
            >
              {/* Categories */}
              <div style={{ overflowY: "auto", maxHeight: 220, paddingRight: 6 }}>
                <div style={{ marginBottom: 8, display: "flex", alignItems: "center", gap: 10 }}>
                  <strong style={{ fontSize: 12, color: "#333" }}>Categories</strong>
                  <label style={{ fontSize: 12, color: "#666", display: "flex", alignItems: "center", gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={includeChildren}
                      onChange={(e) => setIncludeChildren(e.target.checked)}
                    />
                    Include children
                  </label>
                </div>
                {budgetTree.map((c) => (
                  <label key={c.categoryNorm} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}>
                    <input
                      type="checkbox"
                      checked={selectedCats.has(c.categoryNorm)}
                      onChange={() => toggleCat(c.categoryNorm)}
                    />
                    <span style={{ fontSize: 13 }}>{c.category}</span>
                  </label>
                ))}
              </div>

              {/* Subcategories for selected categories (or all if none selected) */}
              <div style={{ overflowY: "auto", maxHeight: 220 }}>
                <div style={{ marginBottom: 8 }}>
                  <strong style={{ fontSize: 12, color: "#333" }}>Subcategories</strong>
                </div>
                {(selectedCats.size > 0 ? budgetTree.filter((c) => selectedCats.has(c.categoryNorm)) : budgetTree).map((c) => (
                  <div key={c.categoryNorm} style={{ marginBottom: 6 }}>
                    <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>{c.category}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 6 }}>
                      {c.subcategories.map((s) => (
                        <label key={s.nameNorm} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <input
                            type="checkbox"
                            checked={selectedSubs.has(`${c.categoryNorm}::${s.nameNorm}`)}
                            onChange={() => toggleSub(c.categoryNorm, s.nameNorm)}
                            disabled={selectedCats.size > 0 && includeChildren && selectedCats.has(c.categoryNorm)}
                            title={selectedCats.size > 0 && includeChildren && selectedCats.has(c.categoryNorm) ? "Disable Include children or uncheck the category to select individual subs" : ""}
                          />
                          <span style={{ fontSize: 13 }}>{s.name || "(Uncategorized)"}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Summary card (now filter-aware, with inline trend badge) */}
      <div
        style={{
          background: "white",
          border: "1px solid #e5e5e5",
          borderRadius: 12,
          padding: "1rem",
          marginBottom: "1rem",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        }}
      >
        <div style={{ fontSize: 14, color: "#666" }}>{summaryLabel}</div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginTop: 4,
            flexWrap: "wrap",
          }}
        >
          {/* COMBINE: one big total + trend badge */}
          {!(compareMode === "compare" && selectedCount > 1) ? (
            <div style={{ fontSize: 36, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
              <span>
                {summaryLabel.startsWith("Total received")
                  ? toUSD(totals.income)
                  : summaryLabel.startsWith("Total spent")
                  ? toUSD(-totals.expenses)
                  : toUSD(totals.net)}
              </span>
              {/* trend badge (hide when compare) */}
              <span
                style={{
                  display: "inline-flex", alignItems: "center",
                  height: 36, lineHeight: "36px", padding: "0 10px",
                  borderRadius: 999, border: "1px solid #e5e5e5",
                  color: trendBadge.kind === "good" ? "#00B074" : trendBadge.kind === "bad" ? "#F4C542" : "#555",
                  fontSize: 14, whiteSpace: "nowrap",
                }}
                title="Based on the current series and time grouping"
              >
                <span style={{ fontSize: 16, marginRight: 6 }}>{trendBadge.emoji ?? "·"}</span>
                <span>{trendBadge.text ?? "No clear trend"}</span>
              </span>
            </div>
          ) : (
            /* COMPARE: vertical list of per-selection totals */
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8, width: "100%" }}>
              {buildSelectionBreakdown(drivingRows, selectedCats, selectedSubs).map((row) => (
                <div key={row.label} style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                  <span style={{ color: "#333", fontWeight: 600 }}>
                    {row.type === "income" ? "Total received in" : "Total spent in"} {row.label}
                  </span>
                  <span style={{ fontWeight: 700, color: row.type === "income" ? "#00875A" : "#B00020" }}>
                    {toUSD(row.type === "income" ? row.total : -row.total)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Budget vs Actual — Summary (selected period) */}
      {bvaComputed.rows.length > 0 && (
        <div
          style={{
            background: "white",
            border: "1px solid #e5e5e5",
            borderRadius: 12,
            padding: "1rem",
            marginBottom: "1rem",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <div style={{ fontSize: 14, color: "#666" }}>
              Budget vs Actual — Summary (selected period)
            </div>
            <a href="#bva-by-month" style={{ fontSize: 12, textDecoration: "underline", color: "#0ea5e9" }}>
              See more detail
            </a>
          </div>

          {/* Header row */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "220px 1fr 1fr 1fr",
              gap: 8,
              marginTop: 10,
              alignItems: "center",
            }}
          >
            <div />
            <div style={{ fontSize: 12, color: "#666", fontWeight: 600, textTransform: "uppercase" }}>
              Budgeted
            </div>
            <div style={{ fontSize: 12, color: "#666", fontWeight: 600, textTransform: "uppercase" }}>
              Actual
            </div>
            <div style={{ fontSize: 12, color: "#666", fontWeight: 600, textTransform: "uppercase" }}>
              Variance
            </div>

            {/* Income row */}
            <div style={{ fontSize: 13, color: "#333", fontWeight: 600 }}>Income</div>
            <div style={{ border: "1px solid #eee", borderRadius: 10, padding: 12, color: "#00875A", fontWeight: 700 }}>
              {toUSD(bvaSummary.bInc)}
            </div>
            <div style={{ border: "1px solid #eee", borderRadius: 10, padding: 12, color: "#00875A", fontWeight: 700 }}>
              {toUSD(bvaSummary.aInc)}
            </div>
            <div
              style={{
                border: "1px solid #eee",
                borderRadius: 10,
                padding: 12,
                fontWeight: 700,
                color: (bvaSummary.aInc - bvaSummary.bInc) >= 0 ? "#00B074" : "#F4C542",
              }}
            >
              {toUSD(bvaSummary.aInc - bvaSummary.bInc)}
            </div>

            {/* Expenses row */}
            <div style={{ fontSize: 13, color: "#333", fontWeight: 600 }}>Expenses</div>
            <div style={{ border: "1px solid #eee", borderRadius: 10, padding: 12, color: "#B00020", fontWeight: 700 }}>
              {toUSD(expNoSav.b)}
            </div>
            <div style={{ border: "1px solid #eee", borderRadius: 10, padding: 12, color: "#B00020", fontWeight: 700 }}>
              {toUSD(expNoSav.a)}
            </div>
            <div
              style={{
                border: "1px solid #eee",
                borderRadius: 10,
                padding: 12,
                fontWeight: 700,
                // Expenses are favorable when ACTUAL < BUDGET → show positive when under by doing (budget - actual)
                color: (expNoSav.b - expNoSav.a) >= 0 ? "#00B074" : "#F4C542",
              }}
            >
              {toUSD(expNoSav.b - expNoSav.a)}
            </div>

            {/* Savings row (separate from Expenses) */}
            <div style={{ fontSize: 13, color: "#333", fontWeight: 600 }}>Savings/Goals</div>
            <div style={{ border: "1px solid #eee", borderRadius: 10, padding: 12, color: TRELLI_GOLD, fontWeight: 700 }}>
              {toUSD(bvaSavings.bSav)}
            </div>
            <div style={{ border: "1px solid #eee", borderRadius: 10, padding: 12, color: TRELLI_GOLD, fontWeight: 700 }}>
              {toUSD(bvaSavings.aSav)}
            </div>
            <div
              style={{
                border: "1px solid #eee",
                borderRadius: 10,
                padding: 12,
                fontWeight: 700,
                // Savings favorable when actual >= budget
                color: (bvaSavings.aSav - bvaSavings.bSav) >= 0 ? "#00B074" : "#F4C542",
              }}
            >
              {toUSD(bvaSavings.aSav - bvaSavings.bSav)}
            </div>

            {/* Net row */}
            <div style={{ fontSize: 13, color: "#333", fontWeight: 600 }}>Net</div>
            <div style={{ border: "1px solid #eee", borderRadius: 10, padding: 12, fontWeight: 700 }}>
              {toUSD(bvaSummary.bNet)}
            </div>
            <div style={{ border: "1px solid #eee", borderRadius: 10, padding: 12, fontWeight: 700 }}>
              {toUSD(bvaSummary.aNet)}
            </div>
            <div
              style={{
                border: "1px solid #eee",
                borderRadius: 10,
                padding: 12,
                fontWeight: 700,
                color: (bvaSummary.aNet - bvaSummary.bNet) >= 0 ? "#00B074" : "#F4C542",
              }}
            >
              {toUSD(bvaSummary.aNet - bvaSummary.bNet)}
            </div>
          </div>

          <div style={{ fontSize: 12, color: "#666", marginTop: 10 }}>
            Savings/Goals are treated as expenses. Non-Budget items are excluded. Filters narrow both Budget and Actual.
          </div>
        </div>
      )}

      {/* Budgets (selected months) */}
      <div style={{ background: "white", border: "1px solid #e5e5e5", borderRadius: 12, padding: "1rem", marginBottom: "1rem", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <div style={{ fontSize: 14, color: "#666" }}>Budget Beats & Misses - Cumulative (selected period)</div>
          <a href="#budget-details" style={{ fontSize: 12, textDecoration: "underline", color: "#0ea5e9" }}>
            See more detail
          </a>
        </div>

        <div style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 12,
          marginTop: 10
        }}>
          {/* Top beats */}
          <div style={{ border: "1px solid #eee", borderRadius: 10, padding: 12, background: "#fcfffd" }}>
            <div style={{ fontSize: 12, color: "#00875A", fontWeight: 700, marginBottom: 8 }}>Top beats</div>

            {/* We’ll show cats first then subs, limited by 3 total like you had */}
            <ol style={{ margin: 0, paddingLeft: 16 }}>
              {(beatCatsTop.concat(beatSubsTop)).slice(0, 3).length === 0 ? (
                <div style={{ fontSize: 12, color: "#777" }}>—</div>
              ) : (
                (beatCatsTop.concat(beatSubsTop)).slice(0, 3).map((r) => (
                  <li key={`beat-${r.label}`} style={{ marginBottom: 4, display: "flex", alignItems: "baseline", gap: 8 }}>
                    <span style={{
                      maxWidth: 220, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "#111", fontSize: 13
                    }}>{r.label}</span>
                    <span style={{ marginLeft: "auto", fontSize: 13, fontWeight: 700, color: "#00875A" }}>
                      {toUSD(r.amount)}
                    </span>
                  </li>
                ))
              )}
            </ol>
          </div>

          {/* Worst misses */}
          <div style={{ border: "1px solid #eee", borderRadius: 10, padding: 12, background: "#fffafa" }}>
            <div style={{ fontSize: 12, color: "#B00020", fontWeight: 700, marginBottom: 8 }}>Worst misses</div>

            <ol style={{ margin: 0, paddingLeft: 16 }}>
              {(missCatsTop.concat(missSubsTop)).slice(0, 3).length === 0 ? (
                <div style={{ fontSize: 12, color: "#777" }}>—</div>
              ) : (
                (missCatsTop.concat(missSubsTop)).slice(0, 3).map((r) => (
                  <li key={`miss-${r.label}`} style={{ marginBottom: 4, display: "flex", alignItems: "baseline", gap: 8 }}>
                    <span style={{
                      maxWidth: 220, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "#111", fontSize: 13
                    }}>{r.label}</span>
                    <span style={{ marginLeft: "auto", fontSize: 13, fontWeight: 700, color: "#B00020" }}>
                      {toUSD(r.amount)}
                    </span>
                  </li>
                ))
              )}
            </ol>
          </div>
        </div>
      </div>

      {/* ===== Highlights: Top Movers + Budgets (summary) ===== */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: 12,
          marginBottom: "1rem",
        }}
      >
        {/* Top Movers */}
        <div style={{ background: "white", border: "1px solid #e5e5e5", borderRadius: 12, padding: "1rem", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <div style={{ fontSize: 14, color: "#666" }}>Top movers (selected period)</div>
            <a href="#full-trends" style={{ fontSize: 12, textDecoration: "underline", color: "#0ea5e9" }}>
              See more detail
            </a>
          </div>

          {/* two inner boxes: trending up | trending down */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
            marginTop: 10
          }}>
            {/* Trending up */}
            <div style={{ border: "1px solid #eee", borderRadius: 10, padding: 12, background: "#fcfffd" }}>
              <div style={{ fontSize: 12, color: "#00875A", fontWeight: 700, marginBottom: 8 }}>Trending Favorably</div>

              {/* Categories */}
              <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>Categories</div>
              <ol style={{ margin: 0, paddingLeft: 16, marginBottom: 8 }}>
                {catsImprovingTop.length === 0 ? <div style={{ fontSize: 12, color: "#777" }}>—</div> :
                  catsImprovingTop.map((r) => (
                    <li key={`cu-${r.label}`} style={{ marginBottom: 4, display: "flex", alignItems: "baseline", gap: 8 }}>
                      <span style={{
                        maxWidth: 220, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "#111", fontSize: 13
                      }}>{r.label}</span>
                      <span style={{ marginLeft: "auto", fontSize: 13, fontWeight: 700, color: "#00875A" }}>
                        {fmtSlope(r.slope)}
                      </span>
                    </li>
                  ))
                }
              </ol>

              {/* Subcategories */}
              <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>Subcategories</div>
              <ol style={{ margin: 0, paddingLeft: 16 }}>
                {subsImprovingTop.length === 0 ? <div style={{ fontSize: 12, color: "#777" }}>—</div> :
                  subsImprovingTop.map((r) => (
                    <li key={`su-${r.label}`} style={{ marginBottom: 4, display: "flex", alignItems: "baseline", gap: 8 }}>
                      <span style={{
                        maxWidth: 220, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "#111", fontSize: 13
                      }}>{r.label}</span>
                      <span style={{ marginLeft: "auto", fontSize: 13, fontWeight: 700, color: "#00875A" }}>
                        {fmtSlope(r.slope)}
                      </span>
                    </li>
                  ))
                }
              </ol>
            </div>

            {/* Trending down */}
            <div style={{ border: "1px solid #eee", borderRadius: 10, padding: 12, background: "#fffafa" }}>
              <div style={{ fontSize: 12, color: "#B00020", fontWeight: 700, marginBottom: 8 }}>Trending down</div>

              {/* Categories */}
              <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>Categories</div>
              <ol style={{ margin: 0, paddingLeft: 16, marginBottom: 8 }}>
                {catsWorseningTop.length === 0 ? <div style={{ fontSize: 12, color: "#777" }}>—</div> :
                  catsWorseningTop.map((r) => (
                    <li key={`cd-${r.label}`} style={{ marginBottom: 4, display: "flex", alignItems: "baseline", gap: 8 }}>
                      <span style={{
                        maxWidth: 220, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "#111", fontSize: 13
                      }}>{r.label}</span>
                      <span style={{ marginLeft: "auto", fontSize: 13, fontWeight: 700, color: "#B00020" }}>
                        {fmtSlope(r.slope)}
                      </span>
                    </li>
                  ))
                }
              </ol>

              {/* Subcategories */}
              <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>Subcategories</div>
              <ol style={{ margin: 0, paddingLeft: 16 }}>
                {subsWorseningTop.length === 0 ? <div style={{ fontSize: 12, color: "#777" }}>—</div> :
                  subsWorseningTop.map((r) => (
                    <li key={`sd-${r.label}`} style={{ marginBottom: 4, display: "flex", alignItems: "baseline", gap: 8 }}>
                      <span style={{
                        maxWidth: 220, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "#111", fontSize: 13
                      }}>{r.label}</span>
                      <span style={{ marginLeft: "auto", fontSize: 13, fontWeight: 700, color: "#B00020" }}>
                        {fmtSlope(r.slope)}
                      </span>
                    </li>
                  ))
                }
              </ol>
            </div>
          </div>
        </div>

      </div>

      {/* Line chart (daily or monthly) */}
      {monthly.length > 0 && (
        <div
          style={{
            background: "white",
            border: "1px solid #e5e5e5",
            borderRadius: 12,
            padding: "1rem",
            marginBottom: "1rem",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "12px",
              flexWrap: "wrap",
              marginBottom: 8,
            }}
          >
            <div style={{ fontSize: 14, color: "#666" }}>
              {preset === "thisMonth"
                ? "This Month — Daily Net"
                : preset === "last3"
                ? "Last 3 Months — Net"
                : "Trailing 12 Months — Net"}
            </div>

            {/* legend */}
            {!(compareMode === "compare" && selectedCount > 1) ? (
              <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 18, height: 0, borderTop: "2px solid #00B074", display: "inline-block" }} />
                  <span style={{ fontSize: 10, color: "#111" }}>Net</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 18, height: 0, borderTop: "2px dashed #F4C542", display: "inline-block" }} />
                  <span style={{ fontSize: 10, color: "#111" }}>Avg</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 18, height: 0, borderTop: "2px dashed #000", display: "inline-block" }} />
                  <span style={{ fontSize: 10, color: "#111" }}>Trend</span>
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", justifyContent: "flex-end" }}>
                {compareSeries.map((s, idx) => (
                  <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 18, height: 0, borderTop: `2px solid ${LINE_COLORS[idx % LINE_COLORS.length]}`, display: "inline-block" }} />
                    <span style={{ fontSize: 10, color: "#111", maxWidth: 140, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {s.label}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <svg
            ref={svgRef}
            width="100%"
            viewBox={`0 0 ${width} ${height}`}
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
            style={{ display: "block" }}
          >
            {/* axes */}
            <line x1={pad} y1={height - pad} x2={width - pad} y2={height - pad} stroke="#e5e7eb" />
            <line x1={pad} y1={pad} x2={pad} y2={height - pad} stroke="#e5e7eb" />

            {/* zero line (bolder) + label */}
            {yMin < 0 && yMax > 0 && (
              <>
                <line
                  x1={pad}
                  x2={width - pad}
                  y1={yFor(0)}
                  y2={yFor(0)}
                  stroke="#6b7280"
                  strokeWidth="1"
                  strokeDasharray="6 4"
                />
                <text
                  x={pad - 6}
                  y={yFor(0) - 2}
                  textAnchor="end"
                  fontSize="12"
                  fill="#6b7280"
                >
                  $0
                </text>
              </>
            )}

            {/* average + trend (combine mode only) */}
            {!(compareMode === "compare" && selectedCount > 1) && monthly.length > 0 && (
              <>
                <line
                  x1={pad}
                  x2={width - pad}
                  y1={yFor(avgNet)}
                  y2={yFor(avgNet)}
                  stroke="#F4C542"
                  strokeWidth="1.5"
                  strokeDasharray="4 2"
                />
                <path d={trendPath} fill="none" stroke="#000" strokeWidth="2" strokeDasharray="8 4" />
              </>
            )}

            {/* data lines */}
            {!(compareMode === "compare" && selectedCount > 1) ? (
              <>
                {/* single net line */}
                <path d={linePath} fill="none" stroke="#00B074" strokeWidth="2" />
                {monthly.map((r, i) => (
                  <circle key={i} cx={xFor(i)} cy={yFor(r.net ?? 0)} r={i === hoverIdx ? 4 : 3} fill="#00B074" />
                ))}
              </>
            ) : (
              <>
                {/* one line per selection */}
                {compareSeries.map((s, idx) => {
                  const d = s.values
                    .map((v, i) => `${i === 0 ? "M" : "L"} ${xFor(i)} ${yFor(v ?? 0)}`)
                    .join(" ");
                  const color = LINE_COLORS[idx % LINE_COLORS.length];
                  return (
                    <g key={s.label}>
                      <path d={d} fill="none" stroke={color} strokeWidth="2" />
                      {s.values.map((v, i) => (
                        <circle key={i} cx={xFor(i)} cy={yFor(v ?? 0)} r={i === hoverIdx ? 4 : 3} fill={color} />
                      ))}
                    </g>
                  );
                })}
              </>
            )}
            {monthly.map((r, i) => {
              const parts = String(r.month || "").split("-");
              let label = r.month;

              if (parts.length === 3) {
                // daily -> dd/mm
                label = `${parts[2]}/${parts[1]}`;
              } else if (parts.length === 2) {
                // monthly -> Month name (short)
                const y = parseInt(parts[0], 10);
                const m = parseInt(parts[1], 10) - 1;
                label = new Date(y, m, 1).toLocaleDateString(undefined, { month: "short" });
              }

              return (
                <text key={i} x={xFor(i)} y={height - 6} textAnchor="middle" fontSize="12" fill="#666">
                  {label}
                </text>
              );
            })}
            {hoverIdx != null && (
              <>
                <line x1={xFor(hoverIdx)} x2={xFor(hoverIdx)} y1={pad} y2={height - pad} stroke="#e5e5e5" />

                {!(compareMode === "compare" && selectedCount > 1) ? (
                  /* COMBINE tooltip: Net / Avg / Trend */
                  <>
                    <rect
                      x={Math.min(Math.max(xFor(hoverIdx) - 70, pad), width - pad - 140)}
                      y={pad}
                      width="140"
                      height="84"
                      rx="8"
                      fill="white"
                      stroke="#e5e5e5"
                    />
                    <text x={Math.min(Math.max(xFor(hoverIdx) - 70, pad), width - pad - 140) + 8} y={pad + 16} fontSize="12" fill="#111">
                      {monthly[hoverIdx].month}
                    </text>
                    <text x={Math.min(Math.max(xFor(hoverIdx) - 70, pad), width - pad - 140) + 8} y={pad + 34} fontSize="12" fill="#00B074">
                      Net: {toUSD(monthly[hoverIdx].net)}
                    </text>
                    <text x={Math.min(Math.max(xFor(hoverIdx) - 70, pad), width - pad - 140) + 8} y={pad + 52} fontSize="12" fill="#F4C542">
                      Avg: {toUSD(avgNet)}
                    </text>
                    <text x={Math.min(Math.max(xFor(hoverIdx) - 70, pad), width - pad - 140) + 8} y={pad + 70} fontSize="12" fill="#374151">
                      Trend: {toUSD(trendY(hoverIdx))}
                    </text>
                  </>
                ) : (
                  /* COMPARE tooltip: per selection lines */
                  (() => {
                    const lines = compareSeries.map((s, idx) => ({
                      label: s.label,
                      color: LINE_COLORS[idx % LINE_COLORS.length],
                      value: s.values[hoverIdx] ?? 0,
                    }));
                    const boxW = 220;
                    const boxH = 22 + lines.length * 18;
                    const x0 = Math.min(Math.max(xFor(hoverIdx) - boxW / 2, pad), width - pad - boxW);
                    const y0 = pad;
                    return (
                      <>
                        <rect x={x0} y={y0} width={boxW} height={boxH} rx="8" fill="white" stroke="#e5e5e5" />
                        <text x={x0 + 8} y={y0 + 16} fontSize="12" fill="#111">{monthly[hoverIdx].month}</text>
                        {lines.map((l, i) => (
                          <g key={l.label}>
                            <line x1={x0 + 8} y1={y0 + 24 + i * 18} x2={x0 + 24} y2={y0 + 24 + i * 18} stroke={l.color} strokeWidth="2" />
                            <text x={x0 + 28} y={y0 + 28 + i * 18} fontSize="12" fill="#111">
                              {l.label.length > 22 ? l.label.slice(0, 21) + "…" : l.label}
                            </text>
                            <text x={x0 + boxW - 8} y={y0 + 28 + i * 18} fontSize="12" fill="#111" textAnchor="end">
                              {toUSD(l.value)}
                            </text>
                          </g>
                        ))}
                      </>
                    );
                  })()
                )}
              </>
            )}
          </svg>
        </div>
      )}

      {/* Grouped Bars (Income | Expense | Net) */}
      <div
        style={{
          background: "white",
          border: "1px solid #e5e5e5",
          borderRadius: 12,
          padding: "1rem",
          marginBottom: "1rem",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        }}
      >
        <div style={{ fontSize: 14, color: "#666", marginBottom: 8 }}>
          {preset === "thisMonth" ? "This Month — Bars by Day" : "Bars by Period"}
        </div>

        {(() => {
          const isSubScope = selectedSubs.size > 0 || selectedCats.size === 1;
          const keyForRow = (r) =>
            preset === "thisMonth"
              ? `${r.date.getFullYear()}-${String(r.date.getMonth() + 1).padStart(2, "0")}-${String(r.date.getDate()).padStart(2, "0")}`
              : ymKey(new Date(r.date.getFullYear(), r.date.getMonth(), 1));
          const labelFor = (r) =>
            isSubScope && r.subcategory ? `${r.category}: ${r.subcategory}` : (r.category || "(Uncategorized)");

          // Per-period stacks
          const incStacks = new Map(); // key -> Map<label, val>
          const expStacks = new Map(); // key -> Map<label, magnitude>
          const savStacks = new Map(); // key -> Map<label, magnitude> (savings)
          for (const r of drivingRows) {
            const k = keyForRow(r);
            const lab = labelFor(r);
            if (r._type === "income") {
              if (!incStacks.has(k)) incStacks.set(k, new Map());
              incStacks.get(k).set(lab, (incStacks.get(k).get(lab) ?? 0) + r.amount);
            } else if (r._type === "savings") {
              if (!savStacks.has(k)) savStacks.set(k, new Map());
              savStacks.get(k).set(lab, (savStacks.get(k).get(lab) ?? 0) + (-r.amount));
            } else {
              if (!expStacks.has(k)) expStacks.set(k, new Map());
              expStacks.get(k).set(lab, (expStacks.get(k).get(lab) ?? 0) + (-r.amount));
            }
          }

          // Totals per period (to decide whether to draw Net)
          const incTotals = new Map();
          const expTotals = new Map();
          const savTotals = new Map();
          for (const k of periodKeys) {
            const i = Array.from((incStacks.get(k) ?? new Map()).values()).reduce((a, b) => a + b, 0);
            const e = Array.from((expStacks.get(k) ?? new Map()).values()).reduce((a, b) => a + b, 0);
            const s = Array.from((savStacks.get(k) ?? new Map()).values()).reduce((a, b) => a + b, 0);
            incTotals.set(k, i);
            expTotals.set(k, e);
            savTotals.set(k, s);
          }

          // Global scale (separate max for up/down just like symmetric chart)
          const incomeMax = Math.max(0, ...periodKeys.map((k) => incTotals.get(k) ?? 0));
          const expenseMax = Math.max(0, ...periodKeys.map((k) => expTotals.get(k) ?? 0));
          const savingsMax = Math.max(0, ...periodKeys.map((k) => savTotals.get(k) ?? 0));

          const svgH = 260;
          const laneW = (width - pad * 2) / Math.max(1, periodKeys.length);
          // Decide how many bars per lane
          const laneHas = (k) => {
            const incHasData = (incTotals.get(k) ?? 0) > 0;
            const expHasData = (expTotals.get(k) ?? 0) > 0;
            const savHasData = (savTotals.get(k) ?? 0) > 0;
            return {
              // Always allocate these lanes so group width stays consistent:
              inc: true,
              exp: true,
              sav: true,
              // Always allocate Net; the draw step already shows a $0 stub or a real bar.
              net: true,
              // expose “has data” flags for drawing vs. stubs:
              _incHasData: incHasData, _expHasData: expHasData, _savHasData: savHasData,
            };
          };
          const centerY = (h) => h - pad - (h - pad * 2) / 2;

          // use Trelli ramps (greens for income, reds for expense)
          const colorIncome = incomeColorFor;
          const colorExpense = expenseColorFor;

          // mapping helpers
          const yUp = (val) => centerY(svgH) - (incomeMax ? (val / incomeMax) * (svgH / 2 - pad) : 0);
          const yDownH = (val) => (expenseMax ? (val / expenseMax) * (svgH / 2 - pad) : 0);

          // bar width and spacing (narrower than lane)
          const groupBarCount = (k) => {
            const h = laneHas(k);
            // 3 fixed lanes (Inc | Exp | Sav) + optional Net
            return 3 + (h.net ? 1 : 0);
          };

          const hitRects = [];   // filled as we draw; [{x,y,w,h, type, monthLabel, label, value, total}]

          return (
            <svg
              width="100%"
              viewBox={`0 0 ${width} ${svgH}`}
              style={{ display: "block" }}
              onMouseMove={(e) => {
                const svg = e.currentTarget;
                const { x, y } = svgPoint(e, svg);
                const T = 3; // tolerance
                const hit = hitRects.find(r => x >= r.x - T && x <= r.x + r.w + T && y >= r.y - T && y <= r.y + r.h + T);
                if (!hit) return setBarHover(null);
                setBarHover({
                  x: Math.min(width - pad - 220, Math.max(hit.x + hit.w + 8, pad)),
                  y: Math.max(pad, hit.y - 56),
                  type: hit.type, month: hit.monthLabel, label: hit.label, value: hit.value, total: hit.total
                });
              }}
              onMouseLeave={() => setBarHover(null)}
            >
              {/* axes */}
              <line x1={pad} y1={svgH - pad} x2={width - pad} y2={svgH - pad} stroke="#e5e7eb" />
              <line x1={pad} y1={pad} x2={pad} y2={svgH - pad} stroke="#e5e7eb" />
              {/* zero line */}
              <line x1={pad} x2={width - pad} y1={centerY(svgH)} y2={centerY(svgH)} stroke="#6b7280" strokeDasharray="6 4" />

              {periodKeys.map((k, i) => {
                const xLane = pad + i * laneW;
                const has = laneHas(k);
                const nBars = groupBarCount(k);
                if (nBars === 0) {
                  // label only
                  const lbl = formatKeyLabel(k);
                  return (
                    <g key={k}>
                      <text x={xLane + laneW / 2} y={svgH - 6} textAnchor="middle" fontSize="12" fill="#666">{lbl}</text>
                    </g>
                  );
                }
                // Make bars touch inside the group, but leave space between groups
                const innerGap = 0; // bars within a group touch
                const outerGap = Math.min(70, Math.max(20, laneW * 0.25)); // space around the whole group
                const maxGroupWidth = Math.max(nBars * 5, laneW - outerGap); // never exceed lane (leaves group spacing)
                const barW = Math.max(3, Math.floor((maxGroupWidth - innerGap * (nBars - 1)) / nBars));
                const groupWidth = barW * nBars + innerGap * (nBars - 1);
                const groupStart = xLane + (laneW - groupWidth) / 2;
                const barOffset = (idxInGroup) => groupStart + idxInGroup * (barW + innerGap);

                let groupIdx = 0;

                // INCOME bar (stacked upwards)
                const incEntries = Array.from((incStacks.get(k) ?? new Map()).entries()).sort((a, b) => b[1] - a[1]);
                const incTotal = incTotals.get(k) ?? 0;
                const xInc = barOffset(groupIdx++);

                // EXPENSE bar (stacked downwards)
                const expEntries = Array.from((expStacks.get(k) ?? new Map()).entries()).sort((a, b) => b[1] - a[1]);
                const expTotal = expTotals.get(k) ?? 0;
                const xExp = barOffset(groupIdx++);

                // SAVINGS bar (stacked downwards, gold)
                const savEntries = Array.from((savStacks.get(k) ?? new Map()).entries()).sort((a, b) => b[1] - a[1]);
                const savTotal = savTotals.get(k) ?? 0;
                const xSav = barOffset(groupIdx++);

                // NET bar (single solid)
                const xNet = has.net ? barOffset(groupIdx++) : null;
                const lbl = formatKeyLabel(k);

                return (
                  <g key={k}>
                    {/* income stack */}
                    {has.inc && (() => {
                      let yTop = centerY(svgH);
                      return incEntries.map(([lab, v]) => {
                        const h = incomeMax ? (v / incomeMax) * (svgH / 2 - pad) : 0;
                        yTop -= h;
                        return (
                          <>
                            <rect key={lab} x={xInc} y={yTop} width={barW} height={h} fill={colorIncome(lab)}>
                            </rect>
                            {(hitRects.push({ x: xInc, y: yTop, w: barW, h, type: "inc", monthLabel: k, label: lab, value: v, total: incTotal }), null)}
                          </>
                        );
                      });
                    })()}

                    {/* expense stack */}
                    {has.exp && (() => {
                      let yBot = centerY(svgH);
                      return expEntries.map(([lab, v]) => {
                        const h = yDownH(v);
                        const rectY = yBot;
                        yBot += h;
                        return (
                          <>
                            <rect key={lab} x={xExp} y={rectY} width={barW} height={h} fill={colorExpense(lab)}>
                            </rect>
                            {hitRects.push({ x: xExp, y: rectY, w: barW, h, type: "exp", monthLabel: k, label: lab, value: v, total: expTotal })}
                          </>
                        );
                      });
                    })()}

                    {/* savings stack */}
                    {has.sav && (() => {
                      let yBot = centerY(svgH);
                      return savEntries.map(([lab, v]) => {
                        const h = savingsMax ? (v / savingsMax) * (svgH / 2 - pad) : 0;
                        const rectY = yBot;
                        yBot += h;
                        return (
                          <>
                            <rect key={lab} x={xSav} y={rectY} width={barW} height={h} fill={savingsColorFor(lab)}>
                            </rect>
                            {(hitRects.push({ x: xSav, y: rectY, w: barW, h, type: "sav", monthLabel: k, label: lab, value: v, total: savTotal }), null)}
                          </>
                        );
                      });
                    })()}

                    {/* zero stubs at $0 for empty series (keeps widths consistent) */}
                    {!has._incHasData && (
                      <line
                        x1={xInc} x2={xInc + barW}
                        y1={centerY(svgH)} y2={centerY(svgH)}
                        stroke={TRELLI_GREEN} strokeWidth="2"
                      />
                    )}
                    {!has._expHasData && (
                      <line
                        x1={xExp} x2={xExp + barW}
                        y1={centerY(svgH)} y2={centerY(svgH)}
                        stroke={TRELLI_RED} strokeWidth="2"
                      />
                    )}
                    {!has._savHasData && (
                      <line
                        x1={xSav} x2={xSav + barW}
                        y1={centerY(svgH)} y2={centerY(svgH)}
                        stroke={TRELLI_GOLD} strokeWidth="2"
                      />
                    )}

                    {/* net bar */}
                    {has.net && (() => {
                      const net = incTotal - (expTotal + savTotal);
                      if (net === 0) {
                        // Draw a flat $0 stub for Net (like the other series)
                        const y0 = centerY(svgH);
                        return (
                          <>
                            <line
                              x1={xNet} x2={xNet + barW}
                              y1={y0} y2={y0}
                              stroke="#111" strokeWidth="2"
                            />
                            {(
                              // small height so hit-testing is easy but still centered on the line
                              hitRects.push({
                                x: xNet, y: y0 - 1, w: barW, h: 2,
                                type: "net", monthLabel: k, label: "Net", value: 0, total: 0
                              }),
                              null
                            )}
                          </>
                        );
                      }

                      // Non-zero Net → draw as a proper bar
                      const up = net >= 0;
                      const h = up
                        ? (incomeMax ? (net / incomeMax) * (svgH / 2 - pad) : 0)
                        : (expenseMax ? (-net / expenseMax) * (svgH / 2 - pad) : 0);
                      const y = up ? (centerY(svgH) - h) : centerY(svgH);

                      return (
                        <>
                          <rect x={xNet} y={y} width={barW} height={h} fill="#111" />
                          {(
                            hitRects.push({
                              x: xNet, y, w: barW, h,
                              type: "net", monthLabel: k, label: "Net", value: net, total: net
                            }),
                            null
                          )}
                        </>
                      );
                    })()}

                    {/* wide hit zones for totals */}
                    {(hitRects.push({ x: xInc, y: pad, w: barW, h: svgH - pad*2, type: "inc", monthLabel: k, label: "Income",        value: 0, total: incTotal }), null)}
                    {(hitRects.push({ x: xExp, y: pad, w: barW, h: svgH - pad*2, type: "exp", monthLabel: k, label: "Expenses",      value: 0, total: expTotal }), null)}
                    {(hitRects.push({ x: xSav, y: pad, w: barW, h: svgH - pad*2, type: "sav", monthLabel: k, label: "Savings/Goals", value: 0, total: savTotal }), null)}
                    {(xNet != null) && hitRects.push({ x: xNet, y: pad, w: barW, h: svgH - pad*2, type: "net", monthLabel: k, label: "Net", value: incTotal - (expTotal + savTotal), total: incTotal - (expTotal + savTotal) })}

                    {/* x label */}
                    <text x={xLane + laneW / 2} y={svgH - 6} textAnchor="middle" fontSize="12" fill="#666">
                      {lbl}
                    </text>
                  </g>
                );
              })}
              {barHover && (
                <g transform={`translate(${barHover.x}, ${barHover.y})`}>
                  <rect width="210" height="56" rx="6" fill="#fff" stroke="#e6e6e6" />
                  <text x="8" y="14" fontSize="12" fill="#111" style={{ fontWeight: 600 }}>{barHover.month}</text>
                  {barHover.type === "inc" && (
                    <>
                      <text x="8" y="30" fontSize="12" fill={TRELLI_GREEN}>Income: {toUSD(barHover.total)}</text>
                      {barHover.label !== "Income" && <text x="8" y="44" fontSize="12" fill="#555">{barHover.label}: {toUSD(barHover.value)}</text>}
                    </>
                  )}
                  {barHover.type === "exp" && (
                    <>
                      <text x="8" y="30" fontSize="12" fill={TRELLI_RED}>Expenses: {toUSD(barHover.total)}</text>
                      {barHover.label !== "Expenses" && <text x="8" y="44" fontSize="12" fill="#555">{barHover.label}: {toUSD(barHover.value)}</text>}
                    </>
                  )}
                  {barHover.type === "sav" && (
                    <>
                      <text x="8" y="30" fontSize="12" fill={TRELLI_GOLD}>Savings/Goals: {toUSD(barHover.total)}</text>
                      {barHover.label !== "Savings/Goals" && <text x="8" y="44" fontSize="12" fill="#555">{barHover.label}: {toUSD(barHover.value)}</text>}
                    </>
                  )}
                  {barHover.type === "net" && (
                    <text x="8" y="34" fontSize="12" fill="#111">Net: {toUSD(barHover.value)}</text>
                  )}
                </g>
              )}
            </svg>
          );
        })()}
      </div>

      {/* Pie charts (stacked vertically). Hide irrelevant side when only income or only expense. */}
      {(() => {
        const onlyIncome = totals.expenses === 0 && totals.income > 0;
        const onlyExpense = totals.income === 0 && totals.expenses < 0;
        const kinds = onlyIncome
          ? ["income"]
          : onlyExpense
          ? ["expense", "savings"]
          : ["income", "expense", "savings"];

        return (
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12, marginBottom: "1rem" }}>
            {kinds.map((kind) => {
              const isIncome  = kind === "income";
              const isExpense = kind === "expense";
              const isSavings = kind === "savings";
              const pool = drivingRows.filter((r) =>
                isIncome  ? r._type === "income"  :
                isExpense ? r._type !== "income" && r._type !== "savings" :
                /* savings */ r._type === "savings"
              );
              const isSubScope = selectedSubs.size > 0 || selectedCats.size === 1;
              const sumByLabel = new Map();
              // 1) Build NET per label (signed sums) for the chosen side
              for (const r of pool) {
                const label = isSubScope && r.subcategory ? `${r.category}: ${r.subcategory}` : (r.category || "(Uncategorized)");
                // Use signed amounts; net them within each label
                const amt = r.amount; // income likely >0, expenses likely <0; but refunds/negatives can occur
                sumByLabel.set(label, (sumByLabel.get(label) ?? 0) + amt);
              }

              // 2) Convert to slices:
              //    - Income pie shows positive part of each label’s net (negatives reduce center total, not drawn as slices).
              //    - Expense pie shows positive spend magnitude: positive part of (-net) for each label on the expense side.
              const sliceEntriesAll = Array.from(sumByLabel.entries())
                .map(([lab, sum]) => {
                  const sliceVal = isIncome ? Math.max(0, sum) : Math.max(0, -sum);
                  return [lab, sliceVal];
                })
                .filter(([, v]) => v > 0)
                .sort((a, b) => b[1] - a[1]);

              // 3) Top-10 for legend; extras in a hover chip
              const entries = sliceEntriesAll.slice(0, 10);
              const extras  = sliceEntriesAll.slice(10, 18);

              // 4) Totals used for ARC geometry vs. the donut center text
              const slicesSum = sliceEntriesAll.reduce((a, [, v]) => a + v, 0);
              // Center Total MUST match the rest of the page:
              const centerTotal = slicesSum; // the sum of the entries in this pie

              // 5) Build arcs from *all* slice entries (so the pie is complete), using Trelli ramps
              const size = 300, R = 110, cx = size / 2, cy = size / 2;
              const rInner = 52; // your inner hole

              // build cumulative angles (start/end) for hit-testing — start at 12 o'clock
              let acc = -Math.PI / 2;
              const slices = sliceEntriesAll.map(([lab, v]) => {
                const frac = slicesSum ? v / slicesSum : 0;
                const start = acc, end = acc + frac * Math.PI * 2;
                acc = end;
                const color = isIncome ? incomeColorFor(lab) : isExpense ? expenseColorFor(lab) : savingsColorFor(lab);
                return { label: lab, value: v, start, end, color };
              });

              // draw path arcs — also start at 12 o'clock so drawing matches hit-test
              let arcAcc = -Math.PI / 2;
              const arcs = (slicesSum > 0 ? sliceEntriesAll : []).map(([lab, v]) => {
                const angle = slicesSum ? (v / slicesSum) * Math.PI * 2 : 0;
                const startA = arcAcc;
                const endA = arcAcc + angle;
                arcAcc = endA;

                const large = angle > Math.PI ? 1 : 0;
                const x1 = cx + R * Math.cos(startA), y1 = cy + R * Math.sin(startA);
                const x2 = cx + R * Math.cos(endA),   y2 = cy + R * Math.sin(endA);

                const d = `M ${cx} ${cy} L ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2} Z`;
                const color = isIncome ? incomeColorFor(lab) : isExpense ? expenseColorFor(lab) : savingsColorFor(lab);
                return { d, color, label: lab, value: v };
              });

              // handle single-slice full circle (SVG arc can’t draw 360°)
              const single = sliceEntriesAll.length === 1
                ? {
                    label: sliceEntriesAll[0][0],
                    value: sliceEntriesAll[0][1],
                    color: isIncome ? incomeColorFor(sliceEntriesAll[0][0])
                          : isExpense ? expenseColorFor(sliceEntriesAll[0][0])
                          : savingsColorFor(sliceEntriesAll[0][0]),
                  }
                : null;

              return (
                <div
                  key={kind}
                  style={{ background: "white", border: "1px solid #e5e5e5", borderRadius: 12, padding: "1rem", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}
                >
                  <div style={{ fontSize: 14, color: "#666", marginBottom: 8 }}>
                    {isIncome ? "Income mix" : isExpense ? "Expense mix" : "Savings/Goals mix"}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 16, alignItems: "center" }}>
                    {/* Donut */}
                    <svg
                      width={size}
                      height={size}
                      viewBox={`0 0 ${size} ${size}`}
                      onMouseMove={(e) => {
                        if (!slicesSum) { setPieHover(null); return; }
                        const svg = e.currentTarget;
                        const { x, y } = svgPoint(e, svg);
                        const dx = x - cx, dy = y - cy;
                        const rad = Math.hypot(dx, dy);
                        if (rad < rInner || rad > R) { setPieHover(null); return; }
                        let ang = Math.atan2(dy, dx);
                        let a = ang - (-Math.PI / 2);
                        if (a < 0) a += Math.PI * 2;

                        const hit = slices.find(s => {
                          let s0 = s.start - (-Math.PI / 2); if (s0 < 0) s0 += Math.PI * 2;
                          let s1 = s.end   - (-Math.PI / 2); if (s1 < 0) s1 += Math.PI * 2;
                          return a >= s0 && a < s1;
                        });
                        if (!hit) return setPieHover(null);
                        setPieHover({
                          kind, x: Math.min(size - 200, Math.max(x + 8, 12)),
                          y: Math.max(12, y - 46),
                          label: hit.label, value: hit.value, color: hit.color
                        });
                      }}
                      onMouseLeave={() => setPieHover(null)}
                    >
                      {single ? (
                        <circle cx={cx} cy={cy} r={R} fill={single.color}>
                        </circle>
                      ) : (
                        arcs.map((a, i) => (
                          <path key={i} d={a.d} fill={a.color}>
                          </path>
                        ))
                      )}
                      <circle cx={cx} cy={cy} r={52} fill="#fff" stroke="#f3f4f6" />
                      <text x={cx} y={cy - 4} textAnchor="middle" fontSize="12" fill="#666">Total</text>
                      <text x={cx} y={cy + 14} textAnchor="middle" fontSize="14" fontWeight="700" fill={isIncome ? "#00875A" : isExpense ? "#B00020" : TRELLI_GOLD}>
                        {toUSD(centerTotal)}
                      </text>
                      {pieHover && pieHover.kind === kind && (
                        <g transform={`translate(${pieHover.x}, ${pieHover.y})`}>
                          <rect width="180" height="40" rx="6" fill="#fff" stroke="#e6e6e6" />
                          <text x="8" y="16" fontSize="15" fill={pieHover.color} style={{ fontWeight: 700 }}>{pieHover.label}</text>
                          <text x="8" y="30" fontSize="15" fill="#111">{toUSD(pieHover.value)}</text>
                        </g>
                      )}
                    </svg>

                    {/* Legend (color-matched, vertical list, close spacing) */}
                    <div>
                      {entries.length === 0 ? (
                        <div style={{ fontSize: 12, color: "#777" }}>No {isIncome ? "income" : isExpense ? "expense" : "savings"} in range.</div>
                      ) : (
                        <>
                          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                            {entries.map(([lab, v]) => {
                              const color = isIncome ? incomeColorFor(lab) : isExpense ? expenseColorFor(lab) : savingsColorFor(lab);
                              return (
                                <li
                                  key={lab}
                                  style={{ marginBottom: 6, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}
                                >
                                  <span
                                    style={{
                                      width: 10, height: 10, borderRadius: 2,
                                      background: color, display: "inline-block", flex: "0 0 auto",
                                    }}
                                  />
                                  <span style={{ fontSize: 14, color: color, fontWeight: 500 }}>{lab}</span>
                                  <span style={{ fontSize: 14, color: color, fontWeight: 700, marginLeft: 6 }}>
                                    {toUSD(v)}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                          {extras.length > 0 && (
                            <div
                              title={extras.map(([lab, v]) => `${lab}: ${toUSD(v)}`).join("\n")}
                              style={{ marginTop: 8, fontSize: 12, color: "#555", display: "inline-flex", alignItems: "center", gap: 6, cursor: "default" }}
                            >
                              <span style={{
                                display: "inline-block", padding: "2px 8px", borderRadius: 999,
                                border: "1px solid #e5e5e5", background: "#fafafa"
                              }}>
                                +{extras.length} more (hover)
                              </span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* Goals — Initial Budget/mo | Avg Saved/mo | Need/mo + cumulative tiny chart */}
      {showGoals && (
        <div
          style={{
            background: "white",
            border: "1px solid #e5e5e5",
            borderRadius: 12,
            padding: "1rem",
            marginBottom: "1rem",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          }}
        >
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ fontSize: 14, color: "#666" }}>Goals</div>
            <div style={{ fontSize: 12, color: "#666" }}>
              Trend starts at your saved amount and compares planned vs actual (selected period).
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12 }}>
            {goalsData.map((g) => {
              // tiny chart dims
              const W = 300, H = 90, P = 14;
              const maxY = Math.max(
                ...g.plannedCum,
                ...g.actualCum,
                g.goal.targetAmount || 0,
                1
              );
              const x = (i) => P + (i * (W - P * 2)) / Math.max(1, g.months.length - 1);
              const y = (v) => H - P - (v / maxY) * (H - P * 2);

              const dPlanned = g.plannedCum.length
                ? g.plannedCum.map((v, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(v)}`).join(" ")
                : "";
              const dActual = g.actualCum.length
                ? g.actualCum.map((v, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(v)}`).join(" ")
                : "";

              return (
                <div key={g.goal.name} style={{ border: "1px solid #eee", borderRadius: 10, padding: 12 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>{g.goal.name}</div>

                  {/* Row of 3 stats */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 8, marginBottom: 8 }}>
                    <div>
                      <div style={{ fontSize: 12, color: "#666" }}>Initial Budget / mo</div>
                      <div style={{ fontSize: 16, fontWeight: 700 }}>{toUSD(g.initialBudgetMonthly)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: "#666" }}>Avg Saved / mo</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: "#00B074" }}>{toUSD(g.avgSavedMonthly)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: "#666" }}>Need to Save / mo</div>
                      <div style={{ fontSize: 16, fontWeight: 700 }}>{toUSD(g.requiredMonthly)}</div>
                    </div>
                  </div>

                  {/* Tiny cumulative chart: planned (dashed) vs actual (green) */}
                  <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ marginBottom: 6 }}>
                    {/* Axes */}
                    <line x1={P} y1={H - P} x2={W - P} y2={H - P} stroke="#e5e7eb" />
                    <line x1={P} y1={P} x2={P} y2={H - P} stroke="#e5e7eb" />

                    {/* Planned linear path from baseline */}
                    {dPlanned && <path d={dPlanned} fill="none" stroke="#6b7280" strokeWidth="2" strokeDasharray="6 4" />}

                    {/* Actual cumulative */}
                    {dActual && <path d={dActual} fill="none" stroke="#00B074" strokeWidth="2" />}

                    {/* Month labels (every month) */}
                    {g.months.map((mk, i) => {
                      const short = formatMonthKey(mk);                  // e.g., "Jan"
                      const lbl = g.months.length > 4 ? short[0] : short; // always label; single letter when long

                      const isFirst = i === 0;
                      const isLast  = i === g.months.length - 1;
                      const anchor  = isFirst ? "start" : isLast ? "end" : "middle";
                      const xPos    = isFirst ? x(i) + 6 : isLast ? x(i) - 6 : x(i);

                      return (
                        <text key={mk} x={xPos} y={H - 2} textAnchor={anchor} fontSize="10" fill="#666">
                          {lbl}
                        </text>
                      );
                    })}
                  </svg>

                  {/* ETA line */}
                  <div style={{ fontSize: 12, color: "#444" }}>{g.etaText}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Budget vs Actual (by month) */}
      {bvaComputed.rows.length > 0 && (
        <div
          id="bva-by-month"
          style={{
            background: "white",
            border: "1px solid #e5e5e5",
            borderRadius: 12,
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            marginBottom: "1rem",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.75rem 1rem", background: "#f9fafb", borderBottom: "1px solid #eee" }}>
            <div style={{ fontSize: 14, color: "#666" }}>
              Budget vs Actual {preset === "thisMonth" ? "(active month)" : "(by month)"}
            </div>
            {preset === "thisMonth" ? (
              <div style={{ fontSize: 12, color: "#666" }}>
                Showing active month (MTD)
              </div>
            ) : (
              <label style={{ fontSize: 12, color: "#666", display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={bvaCompletedOnly}
                  onChange={(e) => setBvaCompletedOnly(e.target.checked)}
                />
                Completed months only
              </label>
            )}
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f9fafb", textAlign: "left" }}>
                <th style={thStyle}>Month</th>
                <th style={thStyle}>
                  Budget Income{includesCurrentMonthMTD ? " (MTD target)" : ""}
                </th>
                <th style={thStyle}>
                  Budget Expenses{includesCurrentMonthMTD ? " (MTD target)" : ""}
                </th>
                <th style={thStyle}>
                  Budget Savings{includesCurrentMonthMTD ? " (MTD target)" : ""}
                </th>
                <th style={thStyle}>
                  Budget Net{includesCurrentMonthMTD ? " (MTD target)" : ""}
                </th>
                <th style={thStyle}>Actual Income</th>
                <th style={thStyle}>Actual Expenses</th>
                <th style={thStyle}>Actual Savings</th>
                <th style={thStyle}>Actual Net</th>
                <th style={thStyle}>Variance</th>
              </tr>
            </thead>
            <tbody>
              {bvaComputed.rows.map((r) => {
                // Compute savings for this month (Budget & Actual)
                let bSavRow = 0;
                const isMTD = !bvaCompletedOnly && r.month === ymKey(new Date());
                if (isMTD) {
                  for (const b of budgetDocs) {
                    if (String(b?.type || "").toLowerCase() !== "savings") continue;
                    const category = b?.category || "";
                    const subs = Array.isArray(b?.subcategories) ? b.subcategories : [];
                    for (const s of subs) {
                      if (!keepBudgetEntry("savings", category, s?.name || "")) continue;
                      bSavRow += Number(expectedToDateForSub(s, false) || 0);
                    }
                  }
                } else {
                  const mm = getBudgetMapForMonthKey(r.month, budgetSnaps, budgetDocs);
                  if (mm instanceof Map) {
                    for (const [k, v] of mm.entries()) {
                      const { type, category, subcategory } = parseKey(k);
                      if (String(type).toLowerCase() !== "savings") continue;
                      if (!keepBudgetEntry(type, category, subcategory)) continue;
                      bSavRow += Number(v) || 0;
                    }
                  }
                }

                let aSavRow = 0;
                for (const tx of drivingRows) {
                  if (tx._type !== "savings") continue;
                  const mk = ymKey(new Date(tx.date.getFullYear(), tx.date.getMonth(), 1));
                  if (mk !== r.month) continue;
                  aSavRow += Math.max(0, -Number(tx.amount || 0));
                }
                // Favorable coloring:
                // - Only income: variance = actualInc - budgetInc (green if ≥ 0)
                // - Only expense: variance = budgetExp - actualExp (green if ≥ 0)
                // - Otherwise (net): variance = actualNet - budgetNet (green if ≥ 0)
                const onlyIncome = totals.expenses === 0 && totals.income > 0;
                const onlyExpense = totals.income === 0 && totals.expenses < 0;
                const favorable = r.variance >= 0;
                const color = favorable ? "#00B074" : "#F4C542";
                return (
                  <tr key={r.month}>
                    <td style={tdStyle}>{formatMonthKey(r.month)}</td>
                    <td style={{ ...tdStyle, color: "#00875A", fontWeight: 600 }}>{toUSD(r.bIncome)}</td>
                    <td style={{ ...tdStyle, color: "#B00020", fontWeight: 600 }}>{toUSD(Math.max(0, (r.bExpense ?? 0) - bSavRow))}</td>
                    <td style={{ ...tdStyle, color: TRELLI_GOLD, fontWeight: 600 }}>{toUSD(bSavRow)}</td>
                    <td style={{ ...tdStyle, fontWeight: 700 }}>{toUSD(r.bNet)}</td>
                    <td style={{ ...tdStyle, color: "#00875A", fontWeight: 600 }}>{toUSD(r.aIncome)}</td>
                    <td style={{ ...tdStyle, color: "#B00020", fontWeight: 600 }}>{toUSD(Math.max(0, (r.aExpense ?? 0) - aSavRow))}</td>
                    <td style={{ ...tdStyle, color: TRELLI_GOLD, fontWeight: 600 }}>{toUSD(aSavRow)}</td>
                    <td style={{ ...tdStyle, fontWeight: 700 }}>{toUSD(r.aNet)}</td>
                    <td style={{ ...tdStyle, fontWeight: 700, color }}>{toUSD(r.variance)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* tiny footnote to mirror your existing counts philosophy */}
          <div style={{ fontSize: 12, color: "#666", padding: "0.5rem 1rem", borderTop: "1px solid #eee" }}>
            Savings/Goals are treated as expenses. Non-Budget items are excluded. Filters narrow both Budget and Actual.
          </div>
        </div>
      )}

      {/* Budget details */}
      <div
        id="budget-details"
        style={{
          background: "white",
          border: "1px solid #e5e5e5",
          borderRadius: 12,
          padding: "1rem",
          marginBottom: "1rem",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <div style={{ fontSize: 14, color: "#666" }}>Budget Beats & Misses - Cumulative (full list)</div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 10 }}>
          {/* Beats (tinted green) */}
          <div style={{ border: "1px solid #eee", borderRadius: 10, padding: 12, background: "#fcfffd" }}>
            <div style={{ fontSize: 12, color: "#00875A", fontWeight: 700, marginBottom: 8 }}>Beats</div>

            {/* Categories */}
            <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>Categories</div>
            <ol style={{ margin: 0, paddingLeft: 16, marginBottom: 8 }}>
              {beatCatsAll.length === 0 ? (
                <div style={{ fontSize: 12, color: "#777" }}>—</div>
              ) : (
                beatCatsAll.map((r) => (
                  <li key={`bcat-${r.label}`} style={{ marginBottom: 4, display: "flex", alignItems: "baseline", gap: 8 }}>
                    <span style={{ maxWidth: 280, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "#111", fontSize: 13 }}>
                      {r.label}
                    </span>
                    <span style={{ marginLeft: "auto", fontSize: 13, fontWeight: 700, color: "#00875A" }}>
                      {toUSD(r.amount)}
                    </span>
                  </li>
                ))
              )}
            </ol>

            {/* Subcategories */}
            <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>Subcategories</div>
            <ol style={{ margin: 0, paddingLeft: 16 }}>
              {beatSubsAll.length === 0 ? (
                <div style={{ fontSize: 12, color: "#777" }}>—</div>
              ) : (
                beatSubsAll.map((r) => (
                  <li key={`bsub-${r.label}`} style={{ marginBottom: 4, display: "flex", alignItems: "baseline", gap: 8 }}>
                    <span style={{ maxWidth: 280, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "#111", fontSize: 13 }}>
                      {r.label}
                    </span>
                    <span style={{ marginLeft: "auto", fontSize: 13, fontWeight: 700, color: "#00875A" }}>
                      {toUSD(r.amount)}
                    </span>
                  </li>
                ))
              )}
            </ol>
          </div>

          {/* Misses (tinted red) */}
          <div style={{ border: "1px solid #eee", borderRadius: 10, padding: 12, background: "#fffafa" }}>
            <div style={{ fontSize: 12, color: "#B00020", fontWeight: 700, marginBottom: 8 }}>Misses</div>

            {/* Categories */}
            <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>Categories</div>
            <ol style={{ margin: 0, paddingLeft: 16, marginBottom: 8 }}>
              {missCatsAll.length === 0 ? (
                <div style={{ fontSize: 12, color: "#777" }}>—</div>
              ) : (
                missCatsAll.map((r) => (
                  <li key={`mcat-${r.label}`} style={{ marginBottom: 4, display: "flex", alignItems: "baseline", gap: 8 }}>
                    <span style={{ maxWidth: 280, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "#111", fontSize: 13 }}>
                      {r.label}
                    </span>
                    <span style={{ marginLeft: "auto", fontSize: 13, fontWeight: 700, color: "#B00020" }}>
                      {toUSD(r.amount)}
                    </span>
                  </li>
                ))
              )}
            </ol>

            {/* Subcategories */}
            <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>Subcategories</div>
            <ol style={{ margin: 0, paddingLeft: 16 }}>
              {missSubsAll.length === 0 ? (
                <div style={{ fontSize: 12, color: "#777" }}>—</div>
              ) : (
                missSubsAll.map((r) => (
                  <li key={`msub-${r.label}`} style={{ marginBottom: 4, display: "flex", alignItems: "baseline", gap: 8 }}>
                    <span style={{ maxWidth: 280, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "#111", fontSize: 13 }}>
                      {r.label}
                    </span>
                    <span style={{ marginLeft: "auto", fontSize: 13, fontWeight: 700, color: "#B00020" }}>
                      {toUSD(r.amount)}
                    </span>
                  </li>
                ))
              )}
            </ol>
          </div>
        </div>
      </div>

      {/* ===== Full Trend List (uses the same logic as Top Movers) ===== */}
      <div
        id="full-trends"
        style={{
          background: "white",
          border: "1px solid #e5e5e5",
          borderRadius: 12,
          padding: "1rem",
          marginBottom: "1rem",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        }}
      >
        <div style={{ fontSize: 14, color: "#666", marginBottom: 8 }}>Full Trend List</div>

        {/* Two columns: Favorable | Needs Attention */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {/* Favorable */}
          <div style={{ border: "1px solid #eee", borderRadius: 10, padding: 12, background: "#fcfffd" }}>
            <div style={{ fontSize: 12, color: "#00875A", fontWeight: 700, marginBottom: 8 }}>Trending Favorably</div>

            {/* Categories */}
            <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>Categories</div>
            {fullCatsImproving.length === 0 ? (
              <div style={{ fontSize: 12, color: "#777", marginBottom: 8 }}>—</div>
            ) : (
              <ol style={{ margin: 0, paddingLeft: 16, marginBottom: 8 }}>
                {fullCatsImproving.map((r) => (
                  <li key={`full-cat-up-${r.label}`} style={{ marginBottom: 4, display: "flex", alignItems: "baseline", gap: 8 }}>
                    <span style={{ maxWidth: 260, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "#111", fontSize: 13 }}>
                      {r.label}
                    </span>
                    <span style={{ marginLeft: "auto", fontSize: 13, fontWeight: 700, color: "#00875A" }}>
                      {fmtSlope(r.slope)}
                    </span>
                  </li>
                ))}
              </ol>
            )}

            {/* Subcategories */}
            <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>Subcategories</div>
            {fullSubsImproving.length === 0 ? (
              <div style={{ fontSize: 12, color: "#777" }}>—</div>
            ) : (
              <ol style={{ margin: 0, paddingLeft: 16 }}>
                {fullSubsImproving.map((r) => (
                  <li key={`full-sub-up-${r.label}`} style={{ marginBottom: 4, display: "flex", alignItems: "baseline", gap: 8 }}>
                    <span style={{ maxWidth: 260, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "#111", fontSize: 13 }}>
                      {r.label}
                    </span>
                    <span style={{ marginLeft: "auto", fontSize: 13, fontWeight: 700, color: "#00875A" }}>
                      {fmtSlope(r.slope)}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </div>

          {/* Needs Attention */}
          <div style={{ border: "1px solid #eee", borderRadius: 10, padding: 12, background: "#fffafa" }}>
            <div style={{ fontSize: 12, color: "#B00020", fontWeight: 700, marginBottom: 8 }}>Trending Down</div>

            {/* Categories */}
            <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>Categories</div>
            {fullCatsWorsening.length === 0 ? (
              <div style={{ fontSize: 12, color: "#777", marginBottom: 8 }}>—</div>
            ) : (
              <ol style={{ margin: 0, paddingLeft: 16, marginBottom: 8 }}>
                {fullCatsWorsening.map((r) => (
                  <li key={`full-cat-down-${r.label}`} style={{ marginBottom: 4, display: "flex", alignItems: "baseline", gap: 8 }}>
                    <span style={{ maxWidth: 260, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "#111", fontSize: 13 }}>
                      {r.label}
                    </span>
                    <span style={{ marginLeft: "auto", fontSize: 13, fontWeight: 700, color: "#B00020" }}>
                      {fmtSlope(r.slope)}
                    </span>
                  </li>
                ))}
              </ol>
            )}

            {/* Subcategories */}
            <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>Subcategories</div>
            {fullSubsWorsening.length === 0 ? (
              <div style={{ fontSize: 12, color: "#777" }}>—</div>
            ) : (
              <ol style={{ margin: 0, paddingLeft: 16 }}>
                {fullSubsWorsening.map((r) => (
                  <li key={`full-sub-down-${r.label}`} style={{ marginBottom: 4, display: "flex", alignItems: "baseline", gap: 8 }}>
                    <span style={{ maxWidth: 260, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "#111", fontSize: 13 }}>
                      {r.label}
                    </span>
                    <span style={{ marginLeft: "auto", fontSize: 13, fontWeight: 700, color: "#B00020" }}>
                      {fmtSlope(r.slope)}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>
      </div>

      {/* Income & Expenses — full ranked lists (selected period) */}
      <div
        style={{
          background: "white",
          border: "1px solid #e5e5e5",
          borderRadius: 12,
          padding: "1rem",
          marginBottom: "1rem",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        }}
      >
        <div style={{ fontSize: 14, color: "#666", marginBottom: 10 }}>
          Income & Expenses — full ranked lists (selected period)
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {/* Income box (light green) */}
          <div style={{ border: "1px solid #eee", borderRadius: 10, padding: 12, background: "#fcfffd" }}>
            <div style={{ fontSize: 12, color: "#00875A", fontWeight: 700, marginBottom: 8 }}>
              Income
            </div>

            {/* Categories */}
            <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>Categories</div>
            <ol style={{ margin: 0, paddingLeft: 16, marginBottom: 8 }}>
              {incomeCatsAll.length === 0 ? (
                <div style={{ fontSize: 12, color: "#777" }}>—</div>
              ) : (
                incomeCatsAll.map((r) => (
                  <li
                    key={`inc-cat-${r.label}`}
                    style={{ marginBottom: 4, display: "flex", alignItems: "baseline", gap: 8 }}
                  >
                    <span
                      style={{
                        maxWidth: 260,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        color: "#111",
                        fontSize: 13,
                      }}
                      title={r.label}
                    >
                      {r.label}
                    </span>
                    <span style={{ marginLeft: "auto", fontSize: 13, fontWeight: 700, color: "#00875A" }}>
                      {toUSD(r.value)}
                    </span>
                  </li>
                ))
              )}
            </ol>

            {/* Subcategories */}
            <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>Subcategories</div>
            <ol style={{ margin: 0, paddingLeft: 16 }}>
              {incomeSubsAll.length === 0 ? (
                <div style={{ fontSize: 12, color: "#777" }}>—</div>
              ) : (
                incomeSubsAll.map((r) => (
                  <li
                    key={`inc-sub-${r.label}`}
                    style={{ marginBottom: 4, display: "flex", alignItems: "baseline", gap: 8 }}
                  >
                    <span
                      style={{
                        maxWidth: 260,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        color: "#111",
                        fontSize: 13,
                      }}
                      title={r.label}
                    >
                      {r.label}
                    </span>
                    <span style={{ marginLeft: "auto", fontSize: 13, fontWeight: 700, color: "#00875A" }}>
                      {toUSD(r.value)}
                    </span>
                  </li>
                ))
              )}
            </ol>
          </div>

          {/* Expenses box (light red) */}
          <div style={{ border: "1px solid #eee", borderRadius: 10, padding: 12, background: "#fffafa" }}>
            <div style={{ fontSize: 12, color: "#B00020", fontWeight: 700, marginBottom: 8 }}>
              Expenses / Savings
            </div>

            {/* Categories */}
            <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>Categories</div>
            <ol style={{ margin: 0, paddingLeft: 16, marginBottom: 8 }}>
              {expenseCatsAll.length === 0 ? (
                <div style={{ fontSize: 12, color: "#777" }}>—</div>
              ) : (
                expenseCatsAll.map((r) => (
                  <li
                    key={`exp-cat-${r.label}`}
                    style={{ marginBottom: 4, display: "flex", alignItems: "baseline", gap: 8 }}
                  >
                    <span
                      style={{
                        maxWidth: 260,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        color: "#111",
                        fontSize: 13,
                      }}
                      title={r.label}
                    >
                      {r.label}
                    </span>
                    <span style={{ marginLeft: "auto", fontSize: 13, fontWeight: 700, color: "#B00020" }}>
                      {toUSD(r.value)}
                    </span>
                  </li>
                ))
              )}
            </ol>

            {/* Subcategories */}
            <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>Subcategories</div>
            <ol style={{ margin: 0, paddingLeft: 16 }}>
              {expenseSubsAll.length === 0 ? (
                <div style={{ fontSize: 12, color: "#777" }}>—</div>
              ) : (
                expenseSubsAll.map((r) => (
                  <li
                    key={`exp-sub-${r.label}`}
                    style={{ marginBottom: 4, display: "flex", alignItems: "baseline", gap: 8 }}
                  >
                    <span
                      style={{
                        maxWidth: 260,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        color: "#111",
                        fontSize: 13,
                      }}
                      title={r.label}
                    >
                      {r.label}
                    </span>
                    <span style={{ marginLeft: "auto", fontSize: 13, fontWeight: 700, color: "#B00020" }}>
                      {toUSD(r.value)}
                    </span>
                  </li>
                ))
              )}
            </ol>
          </div>
        </div>
      </div>

      {/* dev hints */}
      <div style={{ marginTop: "0.75rem", fontSize: 12, color: "#666" }}>
        Included (after filters): {drivingRows.length}
        {" · "} Excluded Non-Budget: {metaCounts.excludedNonBudget}
        {" · "} Unmapped (sign fallback): {metaCounts.unmappedUsedSignFallback}
        {" · "} Excluded Non-Budget (by name): {metaCounts.excludedNonBudgetByName}
      </div>
    </div>
  );
}